import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock, Check, Truck, ClipboardCheck, Send } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
import { Skeleton } from './Skeleton';
import { getZoneForPosition } from '../data/deliveryZones';
import { useT } from '../utils/i18n';
import { saveOrderForTracking, buildShareLink, generateOrderNumber } from '../utils/orderTracking';
import { PostOrderDropSitePhoto } from './DropSitePhotoUpload';
import { AddressAutocomplete } from './AddressAutocomplete';
import { getRegionMeta } from '../utils/region';
import { pushRecentOrder } from '../utils/recentOrders';
import { trackGoogleAdsPurchase } from '../utils/googleAds';
// Format a 10/11-digit phone string into (XXX) XXX-XXXX
function formatPhone(raw: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
// 50/50 Mix is classified HEAVY (12 yd full load) even though the name
// reads "mix" with topsoil — the blend (sand + topsoil) ships in the same
// 12-yd truckload as rock and aggregates.
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return (
    // Fill dirt removed — heavy base material, ships 12 yd/load.
    // 50/50 Mix removed — heavy blend (sand + topsoil) ships 12 yd/load.
    n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips')
  );
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee
  };
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    loadSavedCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo,
    promo,
    applyPromoCode,
    clearPromoCode
  } = useCart();
  const [promoInput, setPromoInput] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  // Auto-prefill promo input if user copied FIRST10 from the PromoBar
  useEffect(() => {
    if (isCartOpen && !promo.code) {
      try {
        const hint = sessionStorage.getItem('promo_hint');
        if (hint) {
          setPromoInput(hint);
          setShowPromoInput(true);
        }
      } catch {}
    }
  }, [isCartOpen, promo.code]);
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // True after the user tries to submit the cart form with missing/invalid
  // required fields. Switches the guidance ring (green, "fill this next") to
  // an error ring (red, "this is missing") on every invalid field, and stays
  // on until the user advances successfully.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  const wasCartOpenRef = useRef(false);
  // Cart delivery data is shared state, but this field is local so shoppers
  // can edit it without a re-render on every keystroke. Always refresh it at
  // the start of a new cart session: an Instant Estimator handoff supplies a
  // newly verified address, zone, and fee immediately before opening the cart.
  // Restricting this sync to the open transition preserves any active edit once
  // the shopper is already inside the drawer.
  useEffect(() => {
    const isOpening = isCartOpen && !wasCartOpenRef.current;
    if (isOpening) {
      setAddressInput(deliveryAddress);
      setAddressError('');
    }
    wasCartOpenRef.current = isCartOpen;
  }, [isCartOpen, deliveryAddress]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  // dumpLocation dropdown was removed — the dump-location and special
  // instructions fields were merged into a single free-text
  // "Delivery Instructions" textarea (see the JSX below). All downstream
  // emails, SMS, and order tracking now use `instructions` as the single
  // source of truth for delivery directions.
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  // Tooltip explaining why same-day morning isn't guaranteed. Opens when
  // the customer clicks the "Not guaranteed" chip inside the time pill.
  // Kept separate from `showTimeDropdown` so the explanation can be read
  // without closing the time selector or vice-versa.
  const [showNotGuaranteedInfo, setShowNotGuaranteedInfo] = useState(false);
  // The dismissible "You don't need to be home" reassurance card was
  // replaced by an inline reassurance line above the Delivery Instructions
  // textarea, so no separate state or persistence is needed anymore.
  // Dump-location dropdown was removed in favor of a single free-text
  // "Delivery Instructions" textarea, so the outside-click handler that
  // previously closed the dropdown is no longer needed.
  // If a previously chosen time slot becomes invalid for the selected day
  // (e.g. user picks Afternoon, then switches to a Saturday — Sat ends 1 PM
  // so afternoon is closed), clear it so they re-select.
  useEffect(() => {
    if (!deliveryDate || !deliveryTime) return;
    const dow = new Date(deliveryDate + 'T12:00:00').getDay();
    if (dow === 0) {
      setDeliveryTime('');
    } else if (dow === 6 && deliveryTime === 'afternoon') {
      setDeliveryTime('');
    }
  }, [deliveryDate, deliveryTime]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [payOnDeliverySubMethod, setPayOnDeliverySubMethod] = useState<'cash' | 'card'>('cash');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderSummaryData, setOrderSummaryData] = useState<any>(null);
  // Surfaced when the order email/submission fails so the customer sees a
  // real error (and a way to retry / call) instead of a fake success screen.
  const [orderError, setOrderError] = useState<string>('');
  // New features state
  const [smsOptIn, setSmsOptIn] = useState(true);
  const {
    totalDeliveryFee
  } = useCart();
  // Material yards = what the customer actually needs. Billed yards
  // (totalYards from context) is what we charge for.
  const materialYards = items.reduce((sum, item) => sum + item.quantity, 0);
  const yardsBilledExtra = 0;
  // WA combined sales tax estimate for Pierce/King area (~10.1%) on materials only.
  // Delivery freight is generally non-taxable in WA when separately stated.
  const TAX_RATE = 0.101;
  // Saturday delivery surcharge — $40 per delivery when the order falls on a Saturday.
  // Multi-load orders pay the weekend surcharge on each load, matching how
  // the base delivery fee scales by deliveryCount.
  const SATURDAY_SURCHARGE_PER_LOAD = 40;
  const isSaturdayDelivery = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() === 6 : false;
  const saturdaySurcharge = isSaturdayDelivery ? SATURDAY_SURCHARGE_PER_LOAD * deliveryCount : 0;
  const discountedSubtotal = Math.max(0, totalPrice - promo.discount);
  // WA combined sales tax (~10.1%) on materials only. Delivery freight is
  // separately stated and remains non-taxable.
  const estimatedTax = Math.round(discountedSubtotal * TAX_RATE * 100) / 100;
  const finalTotal = discountedSubtotal + totalDeliveryFee + saturdaySurcharge + estimatedTax;
  // Same-day cutoff: 12 PM (noon) Mon–Sat. Before noon on a business day,
  // today is still selectable for afternoon / flexible delivery.
  // Otherwise the next business day (skipping Sunday).
  const minDate = (() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 6=Sat
    const hour = now.getHours();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    // Sunday: dispatch closed, earliest delivery is Monday
    if (dow === 0) {
      target.setDate(target.getDate() + 1);
    } else if (hour >= 12) {
      // After noon cutoff on a business day → next day
      target.setDate(target.getDate() + 1);
      // If next day is Sunday, push to Monday
      if (target.getDay() === 0) target.setDate(target.getDate() + 1);
    }
    return fmt(target);
  })();
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  // Wraps the whole Delivery Instructions section (label + textarea + inline
  // "Done — Continue" button). After date/time are picked we scroll THIS to the
  // top of the form so the label, field, and the Done button are all visible
  // above the mobile keyboard — not just the centered textarea.
  const instructionsSectionRef = useRef<HTMLDivElement>(null);
  // The primary "Proceed to Checkout" CTA — used to scroll the user past the
  // mobile keyboard when they finish typing delivery instructions.
  const checkoutCtaRef = useRef<HTMLButtonElement>(null);
  // True while the delivery-instructions textarea holds focus. Drives the
  // inline "Done" button that lets mobile users dismiss the keyboard and jump
  // straight to the checkout CTA instead of manually closing the keyboard and
  // scrolling the half-covered screen.
  const [instructionsFocused, setInstructionsFocused] = useState(false);
  const t = useT();
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    // If the address text matches the already-validated address (from the
    // Hero estimator or the Delivery Zones map page), trust the upstream
    // zone — don't re-geocode. Re-geocoding ambiguous addresses (e.g. a
    // street name without a city) can resolve to a different point and
    // produce a different zone than what the customer was originally
    // shown, which would silently change their delivery fee.
    if (addressInput.trim() === deliveryAddress.trim() && deliveryZone !== null) {
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        // viewbox keeps ambiguous addresses anchored to our service area
        // (roughly South King + Pierce County). bounded=1 forces results
        // to fall inside the box, preventing wrong-city resolutions.
        // Box: left,top,right,bottom = lon_min,lat_max,lon_max,lat_min
        const viewbox = '-122.75,47.75,-121.65,47.00';
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us&viewbox=${viewbox}&bounded=1`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address (include the city).');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, deliveryAddress, deliveryZone, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Block advancing to checkout if ANY required cart-step field is
    // missing. Delivery instructions in particular need to be enforced
    // here — if they're skipped, the customer reaches the checkout view
    // with a silently-disabled Place Order button and no obvious way
    // back to fix the missing field. Catching it here puts focus
    // directly on the unfilled input instead.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !deliveryTime || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      // Focus and scroll to the first missing field so the user is taken
      // directly to what they need to fix.
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !deliveryTime ? timeBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setHasAttemptedSubmit(false);
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation guard — the Place Order button's `disabled` attribute
    // only blocks mouse clicks. A keyboard Enter press inside any of
    // the text inputs can still trigger this form's onSubmit, which
    // previously let an order through with missing fields (most
    // commonly: empty delivery instructions). Re-check the same rules
    // the button uses, scroll the first missing field into view, and
    // bail out before any payment / order side-effects fire.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const isBark = isLightMaterial(item.product);
        const smallLoadThreshold = 10;
        const billableYards = item.quantity;
        const surcharge = (() => {
          if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
          if (billableYards >= smallLoadThreshold) return 1;
          return 1 + (smallLoadThreshold - billableYards) * 0.1;
        })();
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        let total = billableYards * effectivePrice;
        const fullLoad = isBark ? 15 : 12;
        const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isBark ? fullLoad : fullLoad * 2) ? 0.05 : 0;
        const hasBulk = bulkPct > 0;
        if (hasBulk) {
          total = Math.round(total * (1 - bulkPct));
        }
        const bulkNote = hasBulk ? ` (${Math.round(bulkPct * 100)}% bulk discount)` : '';
        const yardsLabel = `${item.quantity} yds`;
        return `${item.product} (${item.category}) — ${yardsLabel}${bulkNote} = ${total}`;
      }).join('\n');
      const deliveryDow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
      const isSatDelivery = deliveryDow === 6;
      const timeLabel = deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? isSatDelivery ? 'Afternoon (12-1 PM)' : 'Afternoon (12-4 PM)' : isSatDelivery ? 'Flexible (before 1 PM)' : 'Flexible';
      const deliveryFeeLabel = totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE';
      const promoLine = promo.code && promo.discount > 0 ? `\nPromo Code: ${promo.code} (${promo.code === 'FAM20' ? '20' : '10'}% off materials, −${promo.discount.toFixed(2)})` : '';
      const saturdayLine = saturdaySurcharge > 0 ? `\nSaturday Delivery Surcharge: ${saturdaySurcharge.toFixed(2)}${deliveryCount > 1 ? ` (${deliveryCount} × ${SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})` : ''}` : '';
      const subMethodLabel = payOnDeliverySubMethod.charAt(0).toUpperCase() + payOnDeliverySubMethod.slice(1);
      const paymentLabel = paymentMethod === 'card' ? 'Pay Online' : `Pay on Delivery (${subMethodLabel})`;
      // Order-source analytics — tag the email so you can see whether the
      // customer built this order in the Instant Estimator, the Shopping Cart
      // (Product page / Materials hub), or a mix of both.
      const sourceTags = new Set(items.map(it => (it as any).source || 'cart'));
      // If every item came from the Hero estimator, this is a pure
      // estimator handoff. If sources are mixed, fall through to "Mixed"
      // so dispatch can see the order was a combo flow.
      const isEstimatorHandoff = sourceTags.size === 1 && sourceTags.has('estimator');
      const isMixedSource = sourceTags.size > 1;
      const orderSourceLabel = isEstimatorHandoff ? 'Instant Estimator' : isMixedSource ? 'Mixed (Estimator + Cart)' : 'Direct Cart';
      // Yards breakdown for the email body
      const yardsLine = `Total Yards: ${totalYards}`;
      const orderSummary = `Order Summary:\n${orderItems}\n\n${yardsLine}\nMaterials Total: ${totalPrice.toFixed(2)}${promoLine}\nDelivery Fee: ${deliveryFeeLabel}${saturdayLine}\nSales Tax (est. ${(TAX_RATE * 100).toFixed(1)}%): ${estimatedTax.toFixed(2)}\nOrder Total: ${finalTotal.toFixed(2)}\n\nOrder Source: ${orderSourceLabel}\n\nDelivery Address: ${addressInput}\nRequested Date: ${deliveryDate}\nTime Window: ${timeLabel}\nDelivery Instructions: ${instructions || 'Not specified'}\nPayment: ${paymentLabel}`;
      const phoneFormatted = (() => {
        const d = (phone || '').replace(/\D/g, '');
        const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
        return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
      })();
      // Build the order record up-front so we can include a live tracking
      // link inside the confirmation email body itself.
      // Order number format: 206-<CATEGORY>-<SEQ> (e.g. 206-BC-4821 for a
      // Base & Compaction order, 206-MX-4821 for a mixed-category cart).
      // The last four digits are randomized per order.
      const orderNumber = generateOrderNumber(items);
      // Unified brand: every order is 206Gravel regardless of which domain
      // the customer arrived on. getRegionMeta() always resolves to 206.
      const regionCode = getRegionMeta().code;
      const brandName = `${regionCode} Gravel`;
      const brandCompact = `${regionCode}Gravel`;
      const brandDomain = `${regionCode}gravel.com`;
      const trackedOrderPayload = {
        orderId: orderNumber,
        createdAt: Date.now(),
        address: addressInput,
        name,
        phone,
        email,
        date: deliveryDate,
        time: timeLabel,
        instructions,
        items: items.map(it => ({
          product: it.product,
          quantity: it.quantity,
          pricePerYard: it.pricePerYard
        })),
        totalDeliveryFee: deliveryFee,
        saturdaySurcharge: saturdaySurcharge || 0,
        estimatedTax,
        finalTotal
      };
      const trackLink = buildShareLink(trackedOrderPayload);
      const trackOrderSection = `TRACK YOUR ORDER\n\nOrder #: ${orderNumber}\n\nFollow your delivery in real time — confirmed, scheduled, dispatched, en route, delivered:\n${trackLink}\n\nBookmark this link or save this email. It works on any device, no login required.`;
      const whatsNext = `WHAT HAPPENS NEXT\n\n1. SMS Confirmation\n   A text confirmation will arrive at ${phoneFormatted} within a few minutes.\n\n2. Dispatch Review\n   Our team verifies your address, materials, and delivery window — usually within 1 business hour.\n\n3. Driver Dispatched\n   On delivery day, we'll text you a live tracking link the moment your driver is en route.\n\n4. Track Anytime\n   Check the status of your order at any time using your tracking link below.`;
      const howToPrepare = `HOW TO PREPARE FOR DELIVERY\n\n• Clear the dump zone — Mark a flat, accessible spot at least 10' wide x 20' long.\n• Driveway clearance — We need 12' of overhead clearance (watch for branches and wires).\n• Move vehicles & obstacles — Cars, trash bins, hoses, and toys should be clear of the path.\n• Surface protection — If dumping on a finished driveway, lay down plywood or a tarp to protect the surface.\n• Be reachable — Keep your phone nearby on delivery day in case the driver needs directions.\n• Have payment ready — If paying on delivery, please have cash, card, or check available.\n\nFull guide: https://${brandDomain}/delivery-prep`;
      // Property & vehicle safety — the same constraint shown on the order
      // confirmation screen next to the drop-site photo. Repeated here so the
      // customer has it in writing before delivery day.
      const propertySafety = `PROPERTY & VEHICLE SAFETY\n\nWe cannot drive across irrigated, wet, frozen, or landscaped areas to reach the dump spot. Please make sure your delivery instructions keep our truck on gravel or paved surfaces.`;
      // Referral pitch — moved here from the site-wide announcement bar so
      // it reaches paying customers (where it actually has ROI) instead of
      // showing a discount to every visitor. The friend isn't promised a
      // dedicated referral discount (the public FIRST10 already gives
      // first-timers 10% off, so a "FRIEND10" code would be a meaningless
      // duplicate). The real referral incentive is the $25 credit to the
      // original customer — that's clear money in their pocket per
      // referred order, no double-promo confusion.
      const referralSection = `SHARE ${brandCompact.toUpperCase()} — EARN $50 PER REFERRAL\n\nKnow someone else who needs gravel, topsoil, or bark?\n\n  Have them mention your name when they book.\n  Once their order is delivered, we'll credit $50 to your next delivery — automatically.\n\nNo expiration. No minimum. No limit on referrals.`;
      // De-duplicated the "call us" line — the trailing footer already
      // tells the customer to call (206) 619-3742 with questions, so the
      // referral section doesn't need to repeat it.
      const fullEmailBody = `Hi ${name}! Thank you for your order request with ${brandName}. We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\n----------\n\n${trackOrderSection}\n\n----------\n\n${whatsNext}\n\n----------\n\n${howToPrepare}\n\n----------\n\n${propertySafety}\n\n----------\n\n${referralSection}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The ${brandName} Team`;
      // Clear any prior submission error before re-trying.
      setOrderError('');
      const emailOk = await sendFormEmail({
        subject: `${brandCompact} — New Order Submission`,
        to_name: name,
        from_name: brandName,
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Promo Code': promo.code && promo.discount > 0 ? `${promo.code} (−${promo.discount.toFixed(2)})` : 'None',
        'Delivery Fee': deliveryFeeLabel,
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        'Order Source': orderSourceLabel,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: fullEmailBody,
        message: fullEmailBody,
        'Order Number': orderNumber,
        'Track Order Link': trackLink,
        'Track Order Section': trackOrderSection,
        'What Happens Next': whatsNext,
        'How To Prepare': howToPrepare,
        'Property & Vehicle Safety': propertySafety,
        'Delivery Date': deliveryDate,
        'Delivery Time': timeLabel,
        'Delivery Instructions': instructions || 'Not specified',
        'Drop-Site Photo': 'Pending — customer can upload from order success screen',
        'Payment Method': paymentLabel
      });
      // CRITICAL: only transition to the success view if EmailJS actually
      // accepted the order. Previously the code optimistically flipped
      // view='success' regardless of email outcome, which made it look
      // like the order went through even when dispatch never received it.
      if (!emailOk) {
        setOrderError("We couldn't send your order to dispatch just now. Please try again, or call us at (206) 619-3742 and we'll take it over the phone.");
        setIsProcessingPayment(false);
        return;
      }
      // Dispatch has accepted the order, so this is the authoritative purchase
      // confirmation point for Google Ads. The generated order number acts as
      // the transaction ID, which lets Google deduplicate any repeated event.
      let isNewCustomer = false;
      try {
        isNewCustomer = !localStorage.getItem('253gravel_last_order');
      } catch {
        // Tracking may still proceed if browser storage is unavailable; we
        // simply omit the first-order classification in that edge case.
      }
      trackGoogleAdsPurchase({
        transactionId: orderNumber,
        value: finalTotal,
        isNewCustomer
      });
      setOrderSummaryData({
        items: [...items],
        totalYards,
        materialYards,
        yardsBilledExtra,
        totalPrice,
        deliveryFeeLabel,
        saturdaySurcharge,
        estimatedTax,
        finalTotal,
        addressInput,
        deliveryDate,
        timeLabel,
        name,
        phone,
        email,
        orderNumber
      });
      // Append to the recent-orders list FIRST (up to 3, de-duped) so the
      // hero "Reorder last delivery" drawer can offer a choice between the
      // customer's standard loads. This must run BEFORE we overwrite the
      // single-order key below — pushRecentOrder seeds from that key, so
      // overwriting it first would clobber the previous order and we'd only
      // ever keep the latest one.
      pushRecentOrder({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      });
      // Save the single most-recent order for backward-compat (other
      // surfaces and older devices still read this key).
      localStorage.setItem('253gravel_last_order', JSON.stringify({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      }));
      // Burn the private FAM20 family code on this device once it's been
      // redeemed on a placed order — it can only be used a single time.
      if (promo.code === 'FAM20') {
        try {
          localStorage.setItem('253gravel_fam20_used', '1');
        } catch {}
      }
      // Save a complete tracked-order record so the customer can track it.
      try {
        saveOrderForTracking(trackedOrderPayload);
      } catch (e) {
        console.error('Could not save tracked order', e);
      }
      // Email accepted AND the success snapshot is staged — now it's safe
      // to clear the live cart and show the confirmation screen. Doing
      // both here (instead of waiting for handleCloseSuccess) means the
      // cart stays empty no matter HOW the customer dismisses the
      // success view (Done, X, backdrop tap, navigating away).
      setView('success');
      clearCart();
      // Tell the Hero instant estimator to return to its default state now
      // that the order is placed (it stays in sync across components via a
      // lightweight window event rather than shared state).
      window.dispatchEvent(new Event('estimatorReset'));
    } catch (err) {
      console.error('Order submission error:', err);
      setOrderError('Something went wrong placing your order. Please try again, or call us at (206) 619-3742.');
    } finally {
      setIsProcessingPayment(false);
    }
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    // Note: the old setDumpLocation('') reset call lived here. The
    // dumpLocation state was removed when the dump-location dropdown
    // got merged into the free-text Delivery Instructions textarea
    // (see the comment near the top of this file). Leaving the setter
    // call behind threw "setDumpLocation is not defined" the moment
    // the customer closed the success screen.
    clearCart();
    closeCart();
    // Return the customer to the home page so they land on the Hero's
    // in-flight greeting — a blinking "order in progress" header with a
    // one-tap link to the live tracking page. usePersonalGreeting reads
    // the just-saved tracked order on mount and renders that surface.
    navigate('/');
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const inputClass = 'cart-input w-full px-4 py-3.5 bg-white dark:bg-[#2e2e2e] backdrop-blur-sm border border-[#e5e5e5] dark:border-white/5 rounded-xl text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-stone-400 focus:ring-2 focus:ring-[#059F40]/50 focus:border-[#059F40] focus:shadow-[0_0_15px_rgba(5,159,64,0.2)] transition-all text-base font-medium dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  const handleReorder = () => {
    const lastOrderStr = localStorage.getItem('253gravel_last_order');
    if (lastOrderStr) {
      const lastOrder = JSON.parse(lastOrderStr);
      if (lastOrder.items && lastOrder.items.length > 0) {
        loadSavedCart(lastOrder.items);
        setAddressInput(lastOrder.addressInput || '');
        setName(lastOrder.name || '');
        setPhone(lastOrder.phone || '');
        setEmail(lastOrder.email || '');
      }
    }
  };
  const getDeliveryDayText = () => {
    if (!deliveryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delDate = new Date(deliveryDate + 'T00:00:00');
    const diffTime = delDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Same-day delivery!';
    if (diffDays === 1) return 'Delivering tomorrow!';
    return `Delivering in ${diffDays} days`;
  };
  return <AnimatePresence data-id="element-209" data-mp="20q3sl34b10g4">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-stone-900/40 dark:bg-black/70 backdrop-blur-md z-[60]" data-id="element-210" data-mp="20o0s1p5izox5" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-211" data-mp="2keuo0tqxgkx1">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className={
        // Glossy dark treatment: deep green-black panel with iridescent sheen,
        // inset highlights, and soft layered shadows.
        'w-full max-w-[900px] backdrop-blur-xl flex flex-col rounded-2xl max-h-[90dvh] overflow-hidden pointer-events-auto transition-colors duration-300 border-2 bg-[#f6f8f9] dark:bg-[#2e2e2e] border-[#d4ddcf] dark:border-[#111111] shadow-[0_2px_8px_rgba(110,140,106,0.12),0_24px_70px_-12px_rgba(110,140,106,0.28)] dark:shadow-[0_24px_70px_-12px_rgba(0,0,0,0.6)] relative'} role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-212" data-mp="25nlzknyrh5lh">
              {/* Header */}
              <div className={'flex items-center justify-between px-8 py-6 border-b shrink-0 transition-colors duration-300 relative z-10 ' + (view === 'checkout' ? 'border-[#6e8c6a]/30 dark:border-white/5 bg-gradient-to-b from-[#6e8c6a]/[0.10] to-transparent dark:from-white/[0.04] dark:to-transparent' : 'border-[#e5e5e5] dark:border-white/5 bg-gradient-to-b from-[#f5f5f5] to-transparent dark:from-white/[0.04] dark:to-transparent')} data-id="element-213" data-mp="2ka2nr2cjglis">
                <div className="flex items-center space-x-4" data-id="element-214" data-mp="qsww1r0j19am">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-[#059F40] hover:text-[#0C2809] dark:text-[#22c95c] dark:hover:text-white transition-colors rounded-lg hover:bg-[#059F40]/15 mr-1" aria-label="Back to cart" data-id="element-215" data-mp="uv9qvkdqjmsm">
                      <ArrowLeft className="w-6 h-6" data-id="element-216" data-mp="34vi22kkx0nt" />
                    </button>}
                  <Truck className={'w-7 h-7 transition-colors ' + (view === 'checkout' ? 'text-[#059F40]' : 'text-brand-charcoal dark:text-white')} data-id="element-217" data-mp="74nuiqd3fqyg" />
                  <h2 id="cart-drawer-title" className={'font-heading text-xl font-bold transition-colors ' + (view === 'checkout' ? 'text-[#0C2809] dark:text-white' : 'text-brand-charcoal dark:text-white')} data-id="element-218" data-mp="c2fkheu6h300">
                    {view === 'cart' ? t('cart.title') : view === 'checkout' ? t('cart.checkout') : t('cart.orderConfirmed')}
                  </h2>
                  {view === 'checkout' && <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#059F40]/15 border border-[#059F40]/35 text-[10px] font-bold uppercase tracking-wider text-[#059F40] dark:text-[#22c95c]" data-id="element-219" data-mp="2dxjeahsw75lq">
                      <Lock className="w-3 h-3" data-id="element-220" data-mp="2mvd1n38oiowr" />
                      Secure Checkout
                    </span>}
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close cart" data-id="element-221" data-mp="2wv3s8tu4cjrk">
                  <X className="w-6 h-6" data-id="element-222" data-mp="31m2fkddiyi4b" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-223" data-mp="3fkagewdom3kr">
                  {/* Sticky Progress Stepper — only when there are items */}
                  {items.length > 0 && <div className="sticky top-0 z-[55] bg-[#fcfcfc]/85 dark:bg-[#2e2e2e] backdrop-blur-md border-b border-[#e5e5e5] dark:border-white/5 px-6 py-2.5" data-id="element-224" data-mp="3u6zd6qgju2ng">
                      <div className="flex items-center justify-between max-w-full" data-id="element-225" data-mp="3l7m25tcs160m">
                        {[{
                  id: 'address',
                  label: 'Address',
                  isComplete: isAddressValid,
                  isActive: !isAddressValid
                }, {
                  id: 'name',
                  label: 'Name',
                  isComplete: isNameValid,
                  isActive: isAddressValid && !isNameValid
                }, {
                  id: 'phone',
                  label: 'Phone',
                  isComplete: isPhoneValid,
                  isActive: isAddressValid && isNameValid && !isPhoneValid
                }, {
                  id: 'email',
                  label: 'Email',
                  isComplete: isEmailValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && !isEmailValid
                }, {
                  id: 'date',
                  label: 'Date',
                  isComplete: isDateValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid
                }, {
                  id: 'time',
                  label: 'Time',
                  isComplete: !!deliveryTime,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime
                }].map((step, idx, arr) => <Fragment key={step.id} data-id="element-226" data-mp="2vwecup0kaux3" data-mpi={idx}>
                            <div className="flex items-center gap-1.5 relative z-10 shrink-0" data-id="element-227" data-mp="2vxpher1u06p9">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300 ${step.isComplete ? 'bg-[#059F40] text-white shadow-[0_0_8px_rgba(5,159,64,0.4)]' : step.isActive ? 'bg-[#059F40] text-white ring-2 ring-[#059F40]/30 shadow-[0_0_12px_rgba(5,159,64,0.5)]' : 'bg-stone-200 dark:bg-white/5 text-stone-400 dark:text-stone-600'}`} data-id="element-228" data-mp="30wahmqh32qdp">
                                {step.isComplete ? <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-229" data-mp="1hzuw4wsvnkup" /> : idx + 1}
                              </div>
                              <span className={`text-[10px] font-semibold tracking-wide hidden sm:block ${step.isComplete ? 'text-[#059F40] dark:text-[#22c95c]' : step.isActive ? 'text-[#059F40] dark:text-[#22c95c]' : 'text-stone-400 dark:text-stone-600'}`} data-id="element-230" data-mp="1d7tawldqejsj">
                                {step.label}
                              </span>
                            </div>
                            {idx < arr.length - 1 && <div className="flex-1 h-px mx-1.5 bg-stone-200 dark:bg-white/5 relative" data-id="element-231" data-mp="1wxx0vabnacdj">
                                <div className="absolute top-0 left-0 h-full bg-[#059F40] transition-all duration-500 shadow-[0_0_8px_rgba(5,159,64,0.4)]" style={{
                      width: step.isComplete ? '100%' : '0%'
                    }} data-id="element-232" data-mp="6kxtgk3rsx4v" />
                              </div>}
                          </Fragment>)}
                      </div>
                    </div>}
                  {/* Items */}
                  <div className="p-8" data-id="element-233" data-mp="2bjxicrqrxp7n">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-234" data-mp="2gl4rau4nkufw">
                        <div className="w-28 h-28 bg-white dark:bg-[#1a1a1a] rounded-full flex items-center justify-center border border-[#e5e5e5] dark:border-white/5 shadow-inner dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-235" data-mp="2kl5hfc146u2s">
                          <Truck className="w-12 h-12 text-brand-muted dark:text-stone-500 opacity-50" data-id="element-236" data-mp="2ucrw5icnvbaa" />
                        </div>
                        <div data-id="element-237" data-mp="34afwqowy7d0d">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-238" data-mp="34b7eowxmhc86">
                            Your truck is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-239" data-mp="wp7fsiqbwxmv">
                            Looks like you haven't added any materials to your
                            truck yet.
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 w-full max-w-[280px]" data-id="element-240" data-mp="ipog8ebl2um6">
                          <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors border border-transparent dark:border-white/5 backdrop-blur-sm w-full dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-241" data-mp="1gcoje1wp065l">
                            Browse Materials
                          </button>
                          {localStorage.getItem('253gravel_last_order') && <button type="button" onClick={handleReorder} className="bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange border border-brand-orange/30 px-10 py-4 rounded-xl font-bold text-lg transition-colors w-full" data-id="element-242" data-mp="1ucg38j9w4gri">
                              Reorder Last Order
                            </button>}
                        </div>
                      </div> : <div className="space-y-5" data-id="element-243" data-mp="14y3f5vwevozl">
                        {items.map(item => <div key={item.id} className="p-5 bg-white dark:bg-[#3b3b3b] backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/5 shadow-sm dark:shadow-[0_4px_20px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.05)] overflow-hidden relative z-10" data-id="element-244" data-mp="19vv6anp8f72q">
                            {/* Top row: category eyebrow + remove button docked top-right */}
                            <div className="flex items-start justify-between gap-3" data-id="element-245" data-mp="1st63m2razqfj">
                              <div className="min-w-0" data-id="element-246" data-mp="2gjs8jl60gulb">
                                <p className="text-[11px] font-bold text-brand-muted dark:text-stone-400 mb-1 uppercase tracking-wider" data-id="element-247" data-mp="2gi7hi5iwnts7">
                                  {item.category}
                                </p>
                                <h4 className="font-heading font-extrabold text-lg leading-snug text-brand-charcoal dark:text-white" data-id="element-248" data-mp="4w6nsw47grdo">
                                  {item.product}
                                </h4>
                              </div>
                              <button onClick={() => removeFromCart(item.id)} className="-mt-1 -mr-1 p-2 rounded-lg text-brand-muted hover:text-brand-orange hover:bg-black/5 dark:hover:bg-white/10 transition-colors shrink-0" title="Remove item" aria-label={`Remove ${item.product} from cart`} data-id="element-249" data-mp="en9npulef0bd">
                                <Trash2 className="w-5 h-5" data-id="element-250" data-mp="3w39frprvb1oy" />
                              </button>
                            </div>

                            {/* Per-yard + rate pill */}
                            {(() => {
                    const isLight = isLightMaterial(item.product);
                    const smallLoadThreshold = 10;
                    const billableYards = item.quantity;
                    const surcharge = (() => {
                      if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
                      if (billableYards >= smallLoadThreshold) return 1;
                      return 1 + (smallLoadThreshold - billableYards) * 0.1;
                    })();
                    const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                    const fullLoadDisc = isLight ? 15 : 12;
                    const bulkPct = item.quantity >= fullLoadDisc * 4 ? 0.1 : item.quantity >= (isLight ? fullLoadDisc : fullLoadDisc * 2) ? 0.05 : 0;
                    const hasBulk = bulkPct > 0;
                    const displayPrice = hasBulk ? Math.ceil(effectivePrice * (1 - bulkPct)) : effectivePrice;
                    return <div className="mt-1 flex items-center flex-wrap gap-x-2 gap-y-1" data-id="element-251" data-mp="2xrf9jb21b616">
                                  <p className="text-sm text-brand-muted dark:text-stone-300 font-semibold" data-id="element-252" data-mp="32oyko23fr2ow">
                                    ${displayPrice}{' '}
                                    <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-253" data-mp="2q74mr6p1i8r8">
                                      / yard
                                    </span>
                                  </p>
                                  {hasBulk && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#059F40] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-254" data-mp="2h5el781g1tfq">
                                      {Math.round(bulkPct * 100)}% Bulk Discount
                                    </span>}
                                  {!hasBulk && billableYards >= 3 && billableYards < smallLoadThreshold && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-255" data-mp="1xiz1miu2qhqv">
                                        Small-load rate
                                      </span>}
                                  {billableYards < 3 && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-256" data-mp="3ce738obuqlgx">
                                      Micro-load delivery
                                    </span>}
                                </div>;
                  })()}

                            {/* Bottom row: stepper (left) + line total (right) */}
                            <div className="mt-4 flex items-center justify-between gap-5" data-id="element-257" data-mp="2tn7b5e928vjf">
                              {/* Quantity stepper + caption */}
                              {(() => {
                      const isBarkMulch = isLightMaterial(item.product);
                      const fullLoad = isBarkMulch ? 15 : 12;
                      const isFullLoadMultiple = item.quantity % fullLoad === 0;
                      const loadCount = item.quantity / fullLoad;
                      const handleMinus = () => {
                        if (isFullLoadMultiple && loadCount > 1) {
                          updateQuantity(item.id, item.quantity - fullLoad);
                        } else {
                          updateQuantity(item.id, item.quantity - 1);
                        }
                      };
                      const maxQty = isBarkMulch ? 60 : 48;
                      const handlePlus = () => {
                        if (item.quantity >= maxQty) return;
                        if (isFullLoadMultiple) {
                          updateQuantity(item.id, Math.min(item.quantity + fullLoad, maxQty));
                        } else {
                          updateQuantity(item.id, Math.min(item.quantity + 1, maxQty));
                        }
                      };
                      const tonsEstimate = (item.quantity * getDensity(item.product)).toFixed(1);
                      return <div className="flex flex-col gap-1.5" data-id="element-258" data-mp="2xs1cbcld1kbw">
                                    <div className="flex items-center bg-[#f5f5f5] dark:bg-[#333333] border border-[#e5e5e5] dark:border-white/5 rounded-lg overflow-hidden w-[150px]" data-id="element-259" data-mp="2stq1ozn8r5sm">
                                      <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Decrease quantity" data-id="element-260" data-mp="3q5ieh0ggsj5n">
                                        <Minus className="w-5 h-5" data-id="element-261" data-mp="3g9ox93rsw1hj" />
                                      </button>
                                      <div className="flex items-center justify-center py-1.5 flex-1 whitespace-nowrap gap-1.5" data-id="element-262" data-mp="37fair3f3guzh">
                                        <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-263" data-mp="32feed32hj1je">
                                          {isFullLoadMultiple ? loadCount : item.quantity}
                                        </span>
                                        <span className="text-[10px] text-brand-muted dark:text-stone-300 uppercase tracking-wider font-bold" data-id="element-264" data-mp="2ogemznnl88jj">
                                          {isFullLoadMultiple ? loadCount === 1 ? 'Load' : 'Loads' : 'yd'}
                                        </span>
                                      </div>
                                      <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Increase quantity" data-id="element-265" data-mp="1d03oqyg8a6q2">
                                        <Plus className="w-5 h-5" data-id="element-266" data-mp="p9her1mnycdf" />
                                      </button>
                                    </div>
                                    <span className="text-xs text-stone-400 dark:text-stone-400 font-medium pl-0.5" data-id="element-267" data-mp="u7sqxj0fkyyt">
                                      {isFullLoadMultiple ? `${item.quantity} yds` : null}
                                      {isFullLoadMultiple ? ' · ' : ''}≈{' '}
                                      {tonsEstimate} tons
                                    </span>
                                  </div>;
                    })()}

                              {/* Line total */}
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const smallLoadThreshold = 10;
                      const billableYards = item.quantity;
                      const surcharge = (() => {
                        if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
                        if (billableYards >= smallLoadThreshold) return 1;
                        return 1 + (smallLoadThreshold - billableYards) * 0.1;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      let itemTotal = billableYards * effectivePrice;
                      const baseProduct = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
                      const aggYards = items.filter(i => baseProduct(i.product) === baseProduct(item.product)).reduce((s, i) => s + i.quantity, 0);
                      const fullLoad = isLight ? 15 : 12;
                      const bulkPct2 = aggYards >= fullLoad * 4 ? 0.1 : aggYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct2 > 0;
                      if (hasBulk) {
                        itemTotal = Math.round(itemTotal * (1 - bulkPct2));
                      }
                      const savingsAmount = hasBulk ? Math.round(billableYards * effectivePrice * bulkPct2) : 0;
                      return <div className="flex flex-col items-end leading-none -mt-[15px] pr-1" data-id="element-268" data-mp="31mq85fgf1j5z">
                                    <p className="font-heading font-extrabold text-2xl text-brand-charcoal dark:text-white" data-id="element-269" data-mp="2ssligzd0qds1">
                                      ${itemTotal}
                                    </p>
                                    {hasBulk && savingsAmount > 0 && <span className="mt-1 text-xs text-[#059F40] font-semibold" data-id="element-270" data-mp="3l9o3yaqufjoe">
                                        ${savingsAmount} savings
                                      </span>}
                                  </div>;
                    })()}
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Delivery Details */}
                  {items.length > 0 && <div className={`px-8 pb-8 pt-8 ${showCalendar ? 'relative z-[200]' : 'relative z-10'}`} data-id="element-271" data-mp="2fdjlf988fvcu">
                      <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-stone-400 tracking-tight mb-5 font-[Montserrat,_sans-serif]" data-id="element-272" data-mp="2bc7w74t9tc56">
                        Delivery Details
                      </h3>
                      <div className="space-y-4 bg-white dark:bg-[#3b3b3b] backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/5 shadow-sm dark:shadow-[0_4px_20px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.05)] p-5" data-id="element-273" data-mp="21mxyn9fnr19o">
                        {/* Address */}
                        <div data-id="element-274" data-mp="31mi099vv6k7z">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-275" data-mp="31n9vgjljufxq">
                            <span className="flex items-center" data-id="element-276" data-mp="3bk5ve0xdttqo">
                              <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-277" data-mp="3bil4qme6uuy3" />
                              Delivery Address
                            </span>
                          </label>
                          <AddressAutocomplete value={addressInput} onChange={setAddressInput} placeholder={`123 Main St, ${getRegionMeta().cities[0]}, WA`} inputClassName={`${inputClass} ${hasAttemptedSubmit && !isAddressValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required variant="light" data-id="element-278" data-mp="3kiq7ukccn76g" />
                          <div aria-live="polite" className="mt-1.5" data-id="element-279" data-mp="365o9951tglb">
                            {isCalculatingDelivery && <Skeleton className="h-5 w-[60%] rounded" data-id="element-280" data-mp="73v3aru2zbh7" />}
                            {addressError && !isCalculatingDelivery && <p className="text-sm text-red-500 font-medium" data-id="element-281" data-mp="2885mo3311azp">
                                {addressError}
                              </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 flex flex-wrap items-center gap-x-1.5 font-medium" data-id="element-282" data-mp="2hyqsobl15hsn">
                                  <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-283" data-mp="2d5fgrw1d2atv" />
                                  <span data-id="element-284" data-mp="2rubp7u7bxpv0">
                                    Zone {deliveryZone}{' '}
                                    <span className="font-bold" data-id="element-285" data-mp="2mws7gkbfauyb">
                                      {deliveryFee === 0 ? '(Free)' : deliveryCount > 1 ? `(+${totalDeliveryFee.toFixed(0)} · ${deliveryCount} loads)` : `(+${deliveryFee})`}
                                    </span>
                                  </span>
                                  <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors whitespace-nowrap" data-id="element-286" data-mp="11jql204agt4z">
                                    View zones
                                  </a>
                                </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange flex items-center font-medium" data-id="element-287" data-mp="38gcbzss9uuec">
                                  <Info className="w-4 h-4 mr-1" data-id="element-288" data-mp="2ugks5bf2qjsf" />
                                  Outside standard zones. We'll call with a
                                  custom quote.
                                </p>}
                          </div>
                        </div>

                        {/* Name */}
                        <div data-id="element-289" data-mp="2pgygh6ew9blq">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-290" data-mp="2pi01o3pmjeh6">
                            <FileText className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-291" data-mp="jmozl9hw65up" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${hasAttemptedSubmit && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={hasAttemptedSubmit && !isNameValid} data-id="element-292" data-mp="37lmeyy95dcev" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-293" data-mp="d6519etbabbw">
                          <div data-id="element-294" data-mp="d40yx1b29kdq">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-295" data-mp="87ac7v4r3qjc">
                              <Phone className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-296" data-mp="gn9k4i6o1tij" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${hasAttemptedSubmit && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isPhoneValid && phone.length > 0 || hasAttemptedSubmit && !isPhoneValid} data-id="element-297" data-mp="pg32wplbnb15" />
                          </div>
                          <div data-id="element-298" data-mp="2yz7zcc52jz5i">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-299" data-mp="2yxwv6b7popdv">
                              <Mail className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-300" data-mp="33vygz3wiv2t1" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${hasAttemptedSubmit && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isEmailValid && email.length > 0 || hasAttemptedSubmit && !isEmailValid} data-id="element-301" data-mp="3dnb8fueerqyj" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-302" data-mp="1kxir08r51zot">
                          <div className={`relative ${showCalendar ? 'z-[100]' : ''}`} data-id="element-303" data-mp="1fz7fzup3xj50">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-304" data-mp="1b1fm4v4wjmy6">
                              <CalendarDays className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-305" data-mp="160jb7xm4v0j9" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : deliveryDate ? 'border-[#059F40] dark:border-[#059F40]' : ''}`} data-id="element-306" data-mp="x2fxhnhe3820">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-307" data-mp="31jcj7gjr61a5">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-308" data-mp="21w9x9ef57ks" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-309" data-mp="3p73s0a633w6o">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#e5e5e5] dark:border-white/5 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-5" data-id="element-310" data-mp="28uf8pzls98ry">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-311" data-mp="1v9xcwh29fik4">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-312" data-mp="1vc9uuu80psx6">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-313" data-mp="2a6wenoafkh6o" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-314" data-mp="avwsp4tmwnhw">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-315" data-mp="2ocyeemyv3cu6">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-316" data-mp="2fct86g5emkhl" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-317" data-mp="381qpafxvqb8r">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-318" data-mp="333fgm8jbm6s4">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-319" data-mp="1mpfhv0w7on6g">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-320" data-mp="1t2jril7w0xxz" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                const cellDow = new Date(dateStr + 'T12:00:00').getDay();
                                const isSat = cellDow === 6;
                                // Dispatch is closed on
                                // Sundays — block the
                                // entire day at the picker
                                // so customers don't pick
                                // a date, see every time
                                // window labeled CLOSED,
                                // and walk away frustrated.
                                const isSun = cellDow === 0;
                                const isDisabled = isPast || isSun;
                                return <button key={dateStr} type="button" disabled={isDisabled} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} title={isSun ? 'Closed Sundays — please choose another day' : isSat && !isPast ? 'Saturday delivery: +$40 weekend surcharge' : undefined} className={`relative text-base font-bold py-2 rounded-xl transition-colors ${isDisabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed line-through decoration-stone-300 dark:decoration-stone-600/60' : isSelected ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : isToday ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-321" data-mp="hs7xir04hcgs" data-mpi={i}>
                                                {day}
                                                {isSat && !isPast && <span className={`absolute -top-0.5 -right-0.5 text-[8px] font-extrabold leading-none px-1 py-0.5 rounded ${isSelected ? 'bg-white/30 text-white' : 'bg-brand-orange/20 text-brand-orange'}`} data-id="element-322" data-mp="1t5h23c1gm2mq">
                                                    +$40
                                                  </span>}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className={`relative ${showTimeDropdown ? 'z-[100]' : ''}`} data-id="element-323" data-mp="11x5y2jzhhulu">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-324" data-mp="xvkhanjtww6d">
                              <Clock className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-325" data-mp="sxaim2ywbdg0" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-326" data-mp="j6od51kw83b1" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : deliveryTime ? 'border-[#059F40] dark:border-[#059F40]' : ''}`} data-id="element-327" data-mp="j4lc7mi7cprj">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white flex items-center gap-2 min-w-0' : 'text-stone-400'} data-id="element-328" data-mp="359llppc0rdn3">
                                <span className="truncate" data-id="element-329" data-mp="3fa4a8xej3ibc">
                                  {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                                </span>
                              </span>
                              {/* Right-side Clock icon. When the user has
                               picked the 7–11 AM window on a same-day order,
                               the Clock glows amber and becomes its own tap
                               target that opens the "not guaranteed"
                               explanation popover (stopPropagation so the
                               outer time-selector button doesn't toggle the
                               dropdown). Otherwise it's just the field's
                               passive affordance. */}
                              {(() => {
                        const d = new Date();
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        const todayStr = `${y}-${m}-${day}`;
                        const isMorningToday = deliveryTime === 'morning' && deliveryDate === todayStr;
                        if (isMorningToday) {
                          return <span role="button" tabIndex={0} aria-label="Same-day morning is not guaranteed. Tap for details." onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowNotGuaranteedInfo(v => !v);
                          }} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              e.preventDefault();
                              setShowNotGuaranteedInfo(v => !v);
                            }
                          }} className="shrink-0 inline-flex items-center justify-center rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#DD4132]/60" data-id="element-330" data-mp="o3wq0anzqasn">
                                      <Clock className="w-5 h-5 text-[#DD4132] dark:text-[#DD4132] shrink-0" style={{
                              filter: 'drop-shadow(0 0 6px rgba(221,65,50,0.6))'
                            }} data-id="element-331" data-mp="10lz8zl8nxgcl" />
                                    </span>;
                        }
                        return <Clock className="w-5 h-5 text-stone-300 shrink-0" data-id="element-332" data-mp="mmpxuzzbdcfh" />;
                      })()}
                            </button>
                            <AnimatePresence data-id="element-333" data-mp="2kzgdlnf41z3y">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 right-0 w-[320px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#e5e5e5] dark:border-white/5 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-3 space-y-1" data-id="element-334" data-mp="2h1aaqqhe83ob">
                                  {(() => {
                          // Gate windows by the selected day's dispatch hours.
                          // Mon–Fri 6a–5p, Sat ends at 1 PM, Sun closed.
                          const dow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
                          const isSat = dow === 6;
                          const isSun = dow === 0;
                          const noDate = !deliveryDate;
                          // Detect same-day: morning windows can't be
                          // guaranteed since trucks are already routed.
                          const todayStr = (() => {
                            const d = new Date();
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })();
                          const isSameDay = deliveryDate === todayStr;
                          // After 10 AM the morning window
                          // (7–11 AM) is physically impossible
                          // for same-day — by then dispatch is
                          // halfway through the morning route
                          // and there's no truck left that can
                          // still hit a 7–11 slot today. Block
                          // the option entirely so customers
                          // don't pick it and then have to be
                          // told no.
                          const currentHour = new Date().getHours();
                          const morningTooLate = isSameDay && currentHour >= 10;
                          const opts = [{
                            value: 'morning',
                            label: morningTooLate ? 'Morning — too late for today' : 'Morning (7–11 AM)',
                            disabled: isSun || morningTooLate,
                            note: isSameDay && !morningTooLate ? 'Best effort — not guaranteed' : null
                          }, {
                            value: 'afternoon',
                            // Saturday: dispatch ends at 1 PM, so afternoon is unavailable
                            label: isSat ? 'Afternoon — Sat ends 1 PM' : 'Afternoon (12–4 PM)',
                            disabled: isSun || isSat,
                            note: isSameDay && !isSat ? 'Recommended for same-day' : null
                          }, {
                            value: 'flexible',
                            label: isSat ? 'Flexible (before 1 PM)' : 'Flexible',
                            disabled: isSun,
                            note: isSameDay ? 'Best chance same-day' : null
                          }];
                          if (noDate) {
                            return <p className="text-xs text-stone-700 dark:text-stone-300 px-3 py-3 font-medium" data-id="element-335" data-mp="awft55u1jdv6">
                                          Pick a date first.
                                        </p>;
                          }
                          return opts.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
                            if (opt.disabled) return;
                            setDeliveryTime(opt.value);
                            setShowTimeDropdown(false);
                            // Continue the auto-advance
                            // chain — once a time window
                            // is picked, take the customer
                            // straight to the delivery
                            // instructions field (the last
                            // required field). Align the whole
                            // section to the top so the label,
                            // textarea, and the inline "Done —
                            // Continue" button all sit above the
                            // mobile keyboard.
                            setTimeout(() => {
                              instructionsSectionRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                              instructionsRef.current?.focus({
                                preventScroll: true
                              });
                            }, 200);
                          }} className={`w-full text-right px-4 py-3 rounded-xl text-base font-bold transition-colors flex items-center justify-between flex-row-reverse ${opt.disabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : deliveryTime === opt.value ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-336" data-mp="1vhqaqvc9ce6">
                                        <div className="flex flex-col items-end" data-id="element-337" data-mp="2trx9vai2u7qu">
                                          <span data-id="element-338" data-mp="37r5am7tw1nx5">{opt.label}</span>
                                          {opt.note && !opt.disabled && <span className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${opt.value === 'morning' ? deliveryTime === opt.value ? 'text-white/85' : 'text-[#DD4132] dark:text-[#DD4132]' : deliveryTime === opt.value ? 'text-white/85' : 'text-green-600 dark:text-green-400'}`} data-id="element-339" data-mp="32s292xd49ztx">
                                              {opt.note}
                                            </span>}
                                        </div>
                                        {opt.disabled && <span className="text-[10px] uppercase tracking-wider font-bold text-[#DD4132]" data-id="element-340" data-mp="ui477a4ik511">
                                            Closed
                                          </span>}
                                      </button>);
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                            {/* Detailed "why isn't same-day morning guaranteed?"
                             popover — opens when the chip in the time pill
                             is clicked. Anchored just below the pill so the
                             customer can read the explanation without
                             losing context. A semi-transparent backdrop
                             catches outside clicks to dismiss. */}
                            <AnimatePresence data-id="element-341" data-mp="3snktiq5kuv93">
                              {showNotGuaranteedInfo && <>
                                  {/* Invisible click-outside catcher */}
                                  <div className="fixed inset-0 z-[110]" onClick={() => setShowNotGuaranteedInfo(false)} data-id="element-342" data-mp="3jno9j1u4ukkd" />
                                  <motion.div initial={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} transition={{
                          duration: 0.18
                        }} role="dialog" aria-modal="true" aria-labelledby="not-guaranteed-title" className="absolute z-[120] top-full mt-2 right-0 w-[340px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#DD4132]/40 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] px-6 py-5" data-id="element-343" data-mp="3no817pvxsvz4">
                                    <div className="flex items-start gap-3 mb-3" data-id="element-344" data-mp="2jhas0ur8lrfb">
                                      <div className="w-9 h-9 rounded-full bg-[#DD4132]/15 border border-[#DD4132]/40 flex items-center justify-center shrink-0" data-id="element-345" data-mp="2xe7p3dxy854n">
                                        <Info className="w-4 h-4 text-[#DD4132] dark:text-[#DD4132]" data-id="element-346" data-mp="24pk1hbpq93nx" />
                                      </div>
                                      <div className="flex-1 min-w-0" data-id="element-347" data-mp="1zp5qbkstxdlo">
                                        <h4 id="not-guaranteed-title" className="text-sm font-extrabold text-stone-900 dark:text-white leading-tight" data-id="element-348" data-mp="2egwqlvk55fq9">
                                          Why isn't same-day morning guaranteed?
                                        </h4>
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-[#DD4132] dark:text-[#DD4132] mt-0.5" data-id="element-349" data-mp="1h1itq50dhr9k">
                                          7–11 AM same-day only
                                        </p>
                                      </div>
                                      <button type="button" onClick={() => setShowNotGuaranteedInfo(false)} aria-label="Close" className="shrink-0 -mt-1 -mr-1 p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-id="element-350" data-mp="3qsjsjrx2nxz7">
                                        <X className="w-4 h-4" data-id="element-351" data-mp="33491thl3beon" />
                                      </button>
                                    </div>

                                    <div className="space-y-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed" data-id="element-352" data-mp="3c3w0vwqck8ej">
                                      <p data-id="element-353" data-mp="mf3ik3pbaofz">
                                        Our morning trucks are dispatched
                                        starting at 6 AM, and most are already
                                        loaded and routed for previously booked
                                        deliveries before the day even starts.
                                      </p>
                                      <p data-id="element-354" data-mp="rra8vi5l97i1">
                                        When you book a morning slot{' '}
                                        <span className="font-bold" data-id="element-355" data-mp="np5jwwq34z1q">
                                          on the same day
                                        </span>
                                        , we squeeze you into open gaps if we
                                        can — but we can't promise the window
                                        because the schedule is already in
                                        motion.
                                      </p>
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-356" data-mp="2365wvuq4klb9">
                                      <button type="button" onClick={() => {
                              setDeliveryTime('flexible');
                              setShowNotGuaranteedInfo(false);
                              // Match the main time-picker:
                              // hand the customer off to the
                              // instructions field once a
                              // time choice is made, aligning the
                              // section to the top so its label,
                              // field, and "Done — Continue" button
                              // are all visible above the keyboard.
                              setTimeout(() => {
                                instructionsSectionRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start'
                                });
                                instructionsRef.current?.focus({
                                  preventScroll: true
                                });
                              }, 200);
                            }} className="px-3 py-2 rounded-lg bg-[#059F40] hover:bg-[#17A347] text-white text-xs font-bold transition-colors" data-id="element-357" data-mp="1z5vjhx006smd">
                                        Switch to Flexible
                                      </button>
                                      <a href="tel:206-619-3742" className="px-3 py-2 rounded-lg border border-stone-300 dark:border-white/5 bg-white/60 dark:bg-[#1a1a1a] hover:bg-white dark:hover:bg-white/10 text-stone-800 dark:text-white text-xs font-bold transition-colors text-center flex items-center justify-center gap-1.5" data-id="element-358" data-mp="uv2q2pnbepen">
                                        <Phone className="w-3.5 h-3.5" data-id="element-359" data-mp="1ikndplueyau9" />
                                        Call to confirm
                                      </a>
                                    </div>
                                  </motion.div>
                                </>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same-day morning warning is now displayed inline
                         inside the time selector pill itself — see the
                         "Not guaranteed" chip rendered next to "Morning
                         (7–11 AM)" above. The standalone amber banner that
                         used to sit here was removed to declutter the
                         checkout form. */}

                        {/* Removed: standalone "Delivering tomorrow!"
                         countdown pill and the dismissible "You don't
                         need to be home" green card. Both
                         added clutter without enough informational
                         value — the date field already communicates
                         when delivery lands, and the dump-location
                         picker directly below makes the no-home-needed
                         workflow self-evident. Customer-feedback driven
                         decluttering pass. */}

                        {/* Delivery Instructions (required) — single
                         free-text field that replaces both the older
                         Dump Location dropdown and the Additional Notes
                         textarea. Customers describe the drop spot and
                         any access notes in their own words, and the
                         inline reassurance row above reminds them they
                         don't need to be home — the most common
                         checkout hesitation.
                         Drop-site photo lives on the SUCCESS screen
                         post-submit so the form stays short. */}
                        <div ref={instructionsSectionRef} className="scroll-mt-4" data-id="element-360" data-mp="3g5id6v907jz6">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2 font-[Inter,_sans-serif]" data-id="element-361" data-mp="29qrgz4utsnbh">
                            <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-362" data-mp="24thjr31737d3" />
                            Delivery Instructions
                            <span className="ml-1.5 text-amber-500 dark:text-amber-400" aria-hidden="true" data-id="element-363" data-mp="cq3o65igxxeg">
                              *
                            </span>
                            <span className="sr-only" data-id="element-364" data-mp="3ox5pqw9ecik">required</span>
                          </label>

                          {/* "You don't need to be home" reassurance — softer
                           treatment than the old dismissible card. Lives
                           inline above the textarea so the message lands
                           right where the customer is wondering whether
                           they need to be there to direct the driver. */}
                          <div className="flex items-start gap-2 mb-2.5 text-xs text-brand-muted dark:text-stone-400 leading-relaxed" data-id="element-365" data-mp="mlom9skf2dix">
                            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#059F40] dark:text-[#22c95c]" strokeWidth={3} data-id="element-366" data-mp="1f4u4e6twgasz" />
                            <p data-id="element-367" data-mp="2ztg3ozg7qeor">
                              <span className="font-bold dark:text-stone-200 text-[#E6AE60]" data-id="element-368" data-mp="2zsehpzxns7s9">
                                You don't need to be home.
                              </span>{' '}
                              Just tell the driver where to dump the load and
                              note any access details — driveway side, gate
                              codes, dogs to avoid, etc.
                            </p>
                          </div>

                          <textarea ref={instructionsRef} value={instructions} onChange={e => setInstructions(e.target.value)} onFocus={() => setInstructionsFocused(true)} onBlur={() =>
                  // Delay so a tap on the inline "Done" button
                  // (which steals focus) still registers before we
                  // hide it.
                  setTimeout(() => setInstructionsFocused(false), 120)} placeholder="e.g. Left side of driveway near the fence. Gate code 1234. Friendly dog in backyard." rows={4} className={`${inputClass} resize-none ${hasAttemptedSubmit && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} aria-required="true" aria-invalid={hasAttemptedSubmit && !instructions.trim()} data-id="element-369" data-mp="2byd4bxjs8su1" />

                          {/* Mobile keyboard escape hatch — while the textarea
                           is focused (keyboard open, covering the lower half of
                           the screen and the checkout CTA), show a button that
                           dismisses the keyboard and scrolls straight down to
                           "Proceed to Checkout". Hidden on md+ where the
                           keyboard isn't an issue. */}
                          {/* Mobile keyboard escape hatch — a small "Next"
                           button docked lower-right (replaces the old
                           full-width green pill). Dismisses the keyboard and
                           scrolls to the checkout CTA. */}
                          {instructionsFocused && <div className="md:hidden mt-3 flex justify-end" data-id="element-370" data-mp="1tqdk7zut7ckv">
                              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => {
                      instructionsRef.current?.blur();
                      setInstructionsFocused(false);
                      // Wait for the keyboard to collapse before
                      // scrolling so the CTA lands in view.
                      setTimeout(() => {
                        checkoutCtaRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center'
                        });
                      }, 180);
                    }} className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#059F40] hover:bg-[#17A347] text-white font-bold text-sm transition-colors" data-id="element-371" data-mp="1ypzwo72tcosm">
                                Next
                                <ArrowRight className="w-4 h-4" data-id="element-372" data-mp="mdl135fuiiw1" />
                              </button>
                            </div>}
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className={`px-8 pb-8 border-t border-stone-200 dark:border-white/5 pt-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent ${showCalendar ? 'relative z-[50]' : ''}`} data-id="element-373" data-mp="vbwnswdlcwj7">
                      <div className="space-y-3 mb-6" data-id="element-374" data-mp="1j3bv73k8641b">
                        {/* Materials row — matches the checkout summary: the
                         name ("Materials") sits on its own line at 18px bold
                         with the price, and the quantity / tons detail drops
                         below at 12px normal weight. */}
                        <div className="flex justify-between items-start gap-3" data-id="element-375" data-mp="3guxzyyas0jg6">
                          <div className="flex flex-col min-w-0" data-id="element-376" data-mp="36qhkw754f6df">
                            <span className="text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-377" data-mp="3aqs2yi66tn8n">
                              Materials
                            </span>
                            <span className="text-[12px] font-normal text-brand-charcoal dark:text-white" data-id="element-378" data-mp="2wxsngg6ent13">
                              {materialYards} yards
                              {yardsBilledExtra > 0 && <span data-id="element-379" data-mp="2n04jczmwt969">
                                  {' '}
                                  · billed as {totalYards} yd (3 yd order min)
                                </span>}{' '}
                              · ≈{' '}
                              {items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity) * getDensity(item.product), 0).toFixed(1)}{' '}
                              tons
                              {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            </span>
                          </div>
                          <span className="shrink-0 text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-380" data-mp="2daas82np0z8z">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>

                        {/* Promo Code — shown as a plain breakdown row (no pill)
                         so it reads as part of the running total, matching the
                         Delivery Fee / Sales Tax rows. */}
                        <div className="py-1" data-id="element-381" data-mp="1pmb5rexezv07">
                          {promo.code ? <div className="flex justify-between items-center" data-id="element-382" data-mp="1pkq9nkvgc408">
                              <span className="flex items-center gap-1.5 text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-383" data-mp="1knq6bhpzlfct">
                                Promo {promo.code}
                                <button type="button" onClick={() => {
                        clearPromoCode();
                        setPromoInput('');
                        try {
                          sessionStorage.removeItem('promo_hint');
                        } catch {}
                      }} className="text-xs font-medium text-brand-charcoal/70 dark:text-white/60 hover:text-brand-charcoal dark:hover:text-white underline ml-1" data-id="element-384" data-mp="1bl8g6s77o2kj">
                                  Remove
                                </button>
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-385" data-mp="2zkavral9k94b">
                                −${promo.discount.toFixed(2)}
                              </span>
                            </div> : !showPromoInput ? <button type="button" onClick={() => setShowPromoInput(true)} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors underline" data-id="element-386" data-mp="38jo6s7p1d5r5">
                              Have a promo code?
                            </button> : <div className="space-y-1.5" data-id="element-387" data-mp="2kv50fxpjr31p">
                              <div className="flex gap-2" data-id="element-388" data-mp="2ktk3y2jo9a17">
                                <input type="text" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const ok = applyPromoCode(promoInput);
                          if (ok) {
                            try {
                              sessionStorage.removeItem('promo_hint');
                            } catch {}
                          }
                        }
                      }} placeholder="Enter code (e.g. FIRST10)" className="flex-1 px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-[#e5e5e5] dark:border-white/5 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 text-sm font-bold uppercase tracking-wider focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:outline-none" data-id="element-389" data-mp="3o5iruam5g6gs" />
                                <button type="button" onClick={() => {
                        const ok = applyPromoCode(promoInput);
                        if (ok) {
                          try {
                            sessionStorage.removeItem('promo_hint');
                          } catch {}
                        }
                      }} className="px-4 py-2 bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white rounded-lg font-bold text-sm border border-transparent dark:border-white/5 transition-colors" data-id="element-390" data-mp="oj2gbw35pn26">
                                  Apply
                                </button>
                              </div>
                              {promo.error && <p className="text-xs text-red-500 dark:text-red-400 font-medium px-1" data-id="element-391" data-mp="16116fertp05w">
                                  {promo.error}
                                </p>}
                            </div>}
                        </div>

                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg backdrop-blur-sm" data-id="element-392" data-mp="23fn47kisc8rk">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-393" data-mp="3e5sjfgu0i2l" />
                            <p className="text-sm text-stone-700 dark:text-stone-300 font-medium" data-id="element-394" data-mp="3ur0dne0t1rn2">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        {(() => {
                  if (isCalculatingDelivery) {
                    return <div className="flex justify-between items-center" data-id="element-395" data-mp="db1zvhuzuy5k">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-396" data-mp="8cqsdds68zqi">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-397" data-mp="2t2pompzutkda">
                                  <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-398" data-mp="18l6zw4w7zm0a" />
                                </span>
                              </div>;
                  }
                  if (deliveryZone === null && addressInput) {
                    return <div className="flex justify-between items-center" data-id="element-399" data-mp="1hk25pfvcqu32">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-400" data-mp="1mcv9o1izxye3">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-401" data-mp="1w8yan4243qzg">
                                  <span className="text-brand-orange text-sm font-bold" data-id="element-402" data-mp="219uclc2wl328">
                                    Custom Quote
                                  </span>
                                </span>
                              </div>;
                  }
                  if (deliveryFee === 0 && addressInput) {
                    return <div className="flex justify-between items-center" data-id="element-403" data-mp="ayq2z0refaf2">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-404" data-mp="3mrpj5if4cvos">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-405" data-mp="3vtfev0g1agsd">
                                  <span className="text-green-500 font-extrabold" data-id="element-406" data-mp="3qv5i4lenv84n">
                                    FREE
                                  </span>
                                </span>
                              </div>;
                  }
                  if (deliveryFee > 0) {
                    const microItems = items.filter(item => item.quantity > 0 && item.quantity < 3);
                    const microCount = microItems.length;
                    // Count standard truckLOADS, not line items — a
                    // single item can span multiple loads (e.g. 24 yd
                    // = two 12 yd loads), each billed the zone fee.
                    const standardCount = items.filter(item => item.quantity >= 3).reduce((sum, item) => {
                      const n = item.product.toLowerCase();
                      const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
                      const fullLoad = isLight ? 15 : 12;
                      return sum + Math.ceil(item.quantity / fullLoad);
                    }, 0);
                    const standardTotal = standardCount * deliveryFee;
                    const microTotal = microCount * 250;
                    return <div className="space-y-3" data-id="element-407" data-mp="eahw5w0zylpb">
                                {standardCount > 0 && <div className="flex justify-between items-start gap-3" data-id="element-408" data-mp="j8069pqr36yw">
                                    <span className="text-lg font-extrabold text-brand-charcoal dark:text-white min-w-0" data-id="element-409" data-mp="590l4s2g5a7h">
                                      Delivery Fee
                                      {deliveryZone !== null && <span className="text-xs font-medium text-brand-charcoal dark:text-white ml-1.5" data-id="element-410" data-mp="2bwr60o2i1dhx">
                                          · Zone {deliveryZone}
                                        </span>}
                                    </span>
                                    <span className="flex flex-col items-end shrink-0 text-right" data-id="element-411" data-mp="224v3n0tdr17w">
                                      <span className="font-extrabold text-lg text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-412" data-mp="1x6a1uwyhc9hc">
                                        ${standardTotal.toFixed(2)}
                                      </span>
                                      {standardCount > 1 && <span className="text-sm text-stone-400 whitespace-nowrap" data-id="element-413" data-mp="2kw4djb72bs00">
                                          ({standardCount} × $
                                          {deliveryFee.toFixed(2)})
                                        </span>}
                                    </span>
                                  </div>}
                                {microCount > 0 && <div className="flex justify-between items-start" data-id="element-414" data-mp="3h5bsv1vpyogc">
                                    <div className="flex flex-col" data-id="element-415" data-mp="3l5dwvhoxftys">
                                      <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-416" data-mp="3l3j81sajbhpf">
                                        Micro-load delivery
                                      </span>
                                      <span className="text-xs font-medium text-brand-charcoal dark:text-white mt-0.5" data-id="element-417" data-mp="3pnu17vohvpu">
                                        {microItems[0].quantity} yd load · flat
                                        fee (under 3 yd)
                                      </span>
                                    </div>
                                    <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-418" data-mp="hona3r54l2ae">
                                      ${microTotal.toFixed(2)}
                                      {microCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-419" data-mp="2jh20zfpndh2y">
                                          ({microCount} × $250.00)
                                        </span>}
                                    </span>
                                  </div>}
                              </div>;
                  }
                  return <div className="flex justify-between items-center" data-id="element-420" data-mp="1y26vnnfs0i5n">
                              <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-421" data-mp="1t67zhdo7hvuo">
                                Delivery Fee
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-422" data-mp="10ic54fm40g12">
                                <span className="text-stone-500 text-sm font-medium" data-id="element-423" data-mp="1f7030sjfh1u6">
                                  Enter address above
                                </span>
                              </span>
                            </div>;
                })()}

                        {/* Saturday Surcharge Row */}
                        {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-424" data-mp="2td4verhjmv90">
                            <span className="text-base font-bold text-[#DD4132] dark:text-[#DD4132]" data-id="element-425" data-mp="2y4d57omscimp">
                              Saturday Delivery
                              <span className="text-xs font-medium text-[#DD4132]/80 dark:text-[#DD4132]/80 ml-1" data-id="element-426" data-mp="2jh1rph71mojy">
                                (weekend surcharge)
                              </span>
                            </span>
                            <span className="font-extrabold text-lg text-[#DD4132] dark:text-[#DD4132]" data-id="element-427" data-mp="3c2bbe6qyd8r4">
                              ${saturdaySurcharge.toFixed(2)}
                            </span>
                          </div>}

                        {/* Sales Tax Row — WA combined ~10.1% on materials */}
                        <div className="flex justify-between items-center" data-id="element-428" data-mp="1roj60ry0ft2q">
                          <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-429" data-mp="1rqnja0hsu4fo">
                            Sales Tax
                            <span className="text-xs font-medium text-brand-charcoal dark:text-white ml-1" data-id="element-430" data-mp="27fa5951yg1x">
                              (est. {(TAX_RATE * 100).toFixed(1)}%)
                            </span>
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-431" data-mp="3okx7mkqbyeir">
                            ${estimatedTax.toFixed(2)}
                          </span>
                        </div>

                        <div className="mt-4 pt-4 border-t border-stone-200 dark:border-white/5 flex justify-between items-end" data-id="element-432" data-mp="3amywrpb7x080">
                          <div className="flex flex-col" data-id="element-433" data-mp="35n2txte9beu1">
                            <span className="text-xs font-bold text-[#059F40] dark:text-[#22c95c] uppercase tracking-wider mb-1" data-id="element-434" data-mp="35ppbmluv3apz">
                              You pay on delivery
                            </span>
                            <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-435" data-mp="yxepbmfpkcth">
                              Total Estimate
                            </span>
                          </div>
                          <span className="sm:text-4xl text-[#059F40] dark:text-[#22c95c] font-[800] font-['Plus_Jakarta_Sans',_sans-serif] text-[24px]" data-id="element-436" data-mp="1bfifkc2mfq00">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {(() => {
                const cartReady = isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !!instructions.trim();
                const firstMissing = !isAddressValid ? 'a delivery address' : !isNameValid ? 'your name' : !isPhoneValid ? 'your phone number' : !isEmailValid ? 'your email' : !isDateValid ? 'a delivery date' : !deliveryTime ? 'a time window' : !instructions.trim() ? 'delivery instructions (where to drop the load)' : null;
                return <div className="space-y-4" data-id="element-437" data-mp="2dtpsa1p5fhk1">
                            {/* "Half-active" pattern: when required
                              fields are missing the button stays
                              clickable but wears a darker, muted green.
                              Tapping it runs the validation guard
                              (scrolls + focuses the first missing
                              field, which gets a green highlight
                              ring). Feels more helpful than a hard-
                              disabled gray button — the customer can
                              still tap it and is taken straight to
                              what they need to fix. */}
                            <button ref={checkoutCtaRef} type="submit" aria-disabled={!cartReady} className={`w-full flex items-center justify-center py-5 rounded-xl font-extrabold text-lg transition-all group text-white cursor-pointer ${cartReady ? 'bg-[#059F40] hover:bg-[#17A347] shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_4px_14px_rgba(5,159,64,0.4)]' : 'bg-[#0e4a2a] hover:bg-[#0e5a32] shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'}`} data-id="element-438" data-mp="3rmp4no3eiz7g">
                              Proceed to Checkout
                              <ArrowRight className={`w-6 h-6 ml-2 transition-transform ${cartReady ? 'group-hover:translate-x-1' : ''}`} data-id="element-439" data-mp="cqnel3qyp3e0" />
                            </button>
                            {!cartReady && firstMissing && <p className="text-center text-sm font-semibold text-amber-600 dark:text-amber-400 -mt-1" data-id="element-440" data-mp="2zsofoalhjna">
                                Add {firstMissing} to continue
                              </p>}
                            <div className="grid grid-cols-2 gap-4" data-id="element-441" data-mp="3q3yvgkl8vlrg">
                              <button type="button" onClick={handleContinueShopping} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/5 bg-white dark:bg-[#222222] backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-442" data-mp="3c7lmjzus0sol">
                                Add More
                              </button>
                              <button type="button" onClick={() => {
                      const cartText = items.map(item => {
                        const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                        const yardsLabel = bumped ? `${item.requestedQuantity} yds material (billed ${item.quantity})` : `${item.quantity} yds`;
                        return `${yardsLabel} - ${item.product} (${item.pricePerYard}/yd)`;
                      }).join('%0D%0A');
                      const yardsLine = yardsBilledExtra > 0 ? `Material Yards: ${materialYards}%0D%0ABilled Yards: ${totalYards} (3 yd order min)` : `Total Yards: ${totalYards}`;
                      const rc = getRegionMeta().code;
                      const mailtoUrl = `mailto:?subject=${rc} Gravel - My Cart Summary&body=Here is my cart summary from ${rc} Gravel:%0D%0A%0D%0A${cartText}%0D%0A%0D%0A${yardsLine}%0D%0AMaterials Total: ${totalPrice.toFixed(2)}%0D%0A%0D%0AView materials at: https://${rc}gravel.com`;
                      window.location.href = mailtoUrl;
                    }} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/5 bg-white dark:bg-[#222222] backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors flex items-center justify-center dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-443" data-mp="2m480pglee63h">
                                <Mail className="w-4 h-4 mr-2" data-id="element-444" data-mp="1pw1v9r20tq3a" />
                                Email Cart
                              </button>
                            </div>
                          </div>;
              })()}
                      {/* "Need more than 12 yards or a custom amount?" line
                       removed — customers can already add multiple loads
                       or custom yardage directly from the product selector,
                       so the call-us message was misleading. */}
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto relative" data-id="element-445" data-mp="3u4pjv28ulxhf">
                  {/* Review header */}
                  <div className="sticky top-0 z-[60] bg-[#059F40] dark:bg-[#00704A] backdrop-blur-md border-b border-[#059F40] dark:border-[#00704A] px-8 py-4 shadow-sm" data-id="element-446" data-mp="1zm0wb3s7xk7">
                    <div className="flex items-center gap-2" data-id="element-447" data-mp="3p5386x8o4paq">
                      <ClipboardCheck className="w-5 h-5 text-white" data-id="element-448" data-mp="3p6o4osejmib8" />
                      <h3 className="font-heading text-base font-extrabold text-white tracking-tight" data-id="element-449" data-mp="3ah8dxcf0icvj">
                        Review your order
                      </h3>
                    </div>
                  </div>

                  <div className="p-8" data-id="element-450" data-mp="31g0t9x5f5ajx">
                    <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-451" data-mp="36elrjr141k5q">
                      {/* Order Summary */}
                      <div data-id="element-452" data-mp="2mtv4aqyhmjhj">
                        <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5 font-[Montserrat,_sans-serif]" data-id="element-453" data-mp="2mt1zvwn0462k">
                          Order Summary
                        </h3>
                        <div className="space-y-3" data-id="element-454" data-mp="2vr5fydf7wamz">
                          {items.map(item => {
                      const materialQty = item.requestedQuantity ?? item.quantity;
                      const isBumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                      return <div key={item.id} className="flex justify-between items-start gap-3 text-base font-bold text-brand-charcoal dark:text-white py-3 border-b border-stone-200 dark:border-white/5 last:border-0" data-id="element-455" data-mp="3fhhe4gyb6kcu">
                                {/* On mobile the product name and the quantity
                                 stack onto two lines; on desktop (sm+) they sit
                                 inline on a single line. Every piece of text in
                                 this row shares the same color, font, size and
                                 weight for a uniform look. */}
                                <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0" data-id="element-456" data-mp="1rjyiq0919904">
                                  <span className="text-[18px] font-bold" data-id="element-457" data-mp="1vltkf5uvloef">
                                    {item.product}
                                  </span>
                                  <span className="text-[12px] font-normal" data-id="element-458" data-mp="yxdrr0wgyyx3">
                                    × {materialQty} yds
                                    {isBumped && <span data-id="element-459" data-mp="17uo0ofltebyu">
                                        {' '}
                                        (billed {item.quantity} yd · 3 yd min)
                                      </span>}{' '}
                                    · ≈{' '}
                                    {(materialQty * getDensity(item.product)).toFixed(1)}{' '}
                                    tons
                                  </span>
                                </div>
                                <span className="shrink-0 text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-460" data-mp="2teazuink3bli">
                                  $
                                  {(() => {
                            const surcharge = (() => {
                              const q = Math.max(3, item.quantity);
                              if (q >= 10) return 1;
                              return 1 + (10 - q) * 0.1;
                            })();
                            return (Math.max(3, item.quantity) * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                          })()}
                                </span>
                              </div>;
                    })}
                          <div className="space-y-3 pt-3" data-id="element-461" data-mp="3y59ws6i8g9m">
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-462" data-mp="3vj0en01zqor">
                              <span data-id="element-463" data-mp="1teqy8i7cnsn7">Subtotal</span>
                              <span data-id="element-464" data-mp="1tcwh7ez78koe">${totalPrice.toFixed(2)}</span>
                            </div>

                            {/* Promo Discount Row — rendered with the same font,
                             color, size and weight as the rest of the summary
                             (no green highlight), and only the promo name. */}
                            {promo.code && promo.discount > 0 && <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-465" data-mp="2c60dz97prdn8">
                                <span data-id="element-466" data-mp="2352gl9rs64dm">Promo {promo.code}</span>
                                <span data-id="element-467" data-mp="234iz1u61li04">−${promo.discount.toFixed(2)}</span>
                              </div>}

                            {/* Delivery Fee Row */}
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-468" data-mp="rpdqi9j8cp8l">
                              <span data-id="element-469" data-mp="wq1fyqrt8y0p">Delivery Fee</span>
                              <span data-id="element-470" data-mp="o6y6le3tedej">
                                {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-471" data-mp="o9kbt738ggtl" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange" data-id="element-472" data-mp="y5vr85y4dms9">
                                    Custom Quote
                                  </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500" data-id="element-473" data-mp="t7lwtxk7yq7p">FREE</span> : deliveryFee > 0 ? <>
                                    {deliveryCount > 1 && <span className="mr-1.5" data-id="element-474" data-mp="5evkhn8em4ww">
                                        ({deliveryCount} × $
                                        {deliveryFee.toFixed(2)})
                                      </span>}
                                    ${totalDeliveryFee.toFixed(2)}
                                  </> : <span data-id="element-475" data-mp="3iw4py581a71m">Based on address</span>}
                              </span>
                            </div>

                            {/* Saturday Surcharge Row */}
                            {saturdaySurcharge > 0 && <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-476" data-mp="2se8feposzzv9">
                                <span data-id="element-477" data-mp="2xdbk290vg42p">
                                  Saturday Delivery
                                  <span className="text-xs font-medium ml-1" data-id="element-478" data-mp="32b3eb9ozo2a2">
                                    (weekend surcharge)
                                  </span>
                                </span>
                                <span data-id="element-479" data-mp="3bcaxekixowjk">
                                  {deliveryCount > 1 && <span className="mr-1.5" data-id="element-480" data-mp="3gavxmjy6rg80">
                                      ({deliveryCount} × $
                                      {SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})
                                    </span>}
                                  ${saturdaySurcharge.toFixed(2)}
                                </span>
                              </div>}

                            {/* Sales Tax Row — WA combined ~10.1% on materials */}
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-481" data-mp="1netp8scao3f2">
                              <span data-id="element-482" data-mp="1jc5egf40smfg">
                                Sales Tax
                                <span className="text-xs font-medium ml-1" data-id="element-483" data-mp="1edlqk95v90kg">
                                  (est. {(TAX_RATE * 100).toFixed(1)}%)
                                </span>
                              </span>
                              <span data-id="element-484" data-mp="271rdn2ke71ng">${estimatedTax.toFixed(2)}</span>
                            </div>

                            <div className="flex justify-between items-center text-[18px] font-bold text-brand-charcoal dark:text-white pt-4 border-t border-stone-200 dark:border-white/5" data-id="element-485" data-mp="2250xygci282k">
                              <span data-id="element-486" data-mp="1scv8pedmu4r2">Total</span>
                              <span data-id="element-487" data-mp="1sc29cyh06hiy">${finalTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* First-time buyer FIRST10 nudge — only shown to
                         customers with no prior order on this device who have
                         NOT yet applied a promo. Once a promo is applied (e.g.
                         in the shopping-cart step) it lives in the breakdown's
                         Promo row above — we do NOT repeat it here as a second
                         "applied / Remove" chip. */}
                        {(() => {
                    const isFirstTimeBuyer = typeof window !== 'undefined' && !localStorage.getItem('253gravel_last_order');
                    // If a promo is already applied, it's shown in the
                    // breakdown above — render nothing here to avoid a
                    // duplicate callout.
                    if (promo.code) return null;
                    if (!isFirstTimeBuyer) return null;
                    return <div className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-[#1a1a1a] border border-amber-400/40 dark:border-amber-500/30" data-id="element-488" data-mp="8272bfu9rei0">
                              <div className="flex items-center gap-2.5 min-w-0" data-id="element-489" data-mp="1lyqkzdlikyn1">
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/90 text-stone-900 shrink-0 font-extrabold text-xs" data-id="element-490" data-mp="1qvqgiukqmizw">
                                  10%
                                </span>
                                <div className="min-w-0" data-id="element-491" data-mp="1zv3qrpgorblo">
                                  <p className="text-sm font-bold text-stone-900 dark:text-amber-200 leading-tight" data-id="element-492" data-mp="24v8bjrommshp">
                                    First-time customer?
                                  </p>
                                  <p className="text-xs text-stone-600 dark:text-amber-300/80 leading-tight mt-0.5" data-id="element-493" data-mp="2emmeyajp8cg4">
                                    Use code{' '}
                                    <span className="font-mono font-bold tracking-wider" data-id="element-494" data-mp="2nnar0zpj82qd">
                                      FIRST10
                                    </span>{' '}
                                    for 10% off materials.
                                  </p>
                                </div>
                              </div>
                              <button type="button" onClick={() => applyPromoCode('FIRST10')} className="shrink-0 px-3 py-2 bg-stone-900 hover:bg-stone-800 dark:bg-amber-400 dark:hover:bg-amber-300 dark:text-stone-900 text-white rounded-lg text-xs font-extrabold uppercase tracking-wider transition-colors" data-id="element-495" data-mp="nigeekuh5g8m">
                                Apply
                              </button>
                            </div>;
                  })()}
                      </div>

                      {/* Delivery Details Summary (Read-Only) */}
                      <div className="mt-[30px]" data-id="element-496" data-mp="1g9glj4o3r04i">
                        <div className="flex items-center justify-between mb-4" data-id="element-497" data-mp="1g9qen14g1nef">
                          <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight font-[Montserrat,_sans-serif]" data-id="element-498" data-mp="3n0fzdqyv823k">
                            Delivery Details
                          </h3>
                          <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-499" data-mp="1xjaykzr52tjc">
                            Edit
                          </button>
                        </div>
                        <div className="space-y-4" data-id="element-500" data-mp="1sqrdziu1ay51">
                          <div className="flex items-start gap-3" data-id="element-501" data-mp="1es1ejwjqsmdi">
                            <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-502" data-mp="1eqgn4fsq5jjv" />
                            <div data-id="element-503" data-mp="1jspfpb1oqjlg">
                              {/* Address split into two lines: the street sits
                               on line 1 and the city / state / zip wrap onto
                               line 2. We split on the first comma so anything
                               after it (e.g. "Kent, WA, 98042") forms the
                               second line. Falls back to a single line if the
                               address has no comma. */}
                              {(() => {
                          const parts = addressInput.split(',');
                          const street = parts[0].trim();
                          const rest = parts.slice(1).map(p => p.trim()).filter(Boolean).join(', ');
                          return <p className="text-base text-brand-charcoal dark:text-white" data-id="element-504" data-mp="q57ba4etiq2v">
                                    {street}
                                    {rest && <>
                                        <br data-id="element-505" data-mp="gf5odfy7bdpj" />
                                        {rest}
                                      </>}
                                  </p>;
                        })()}
                              {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-506" data-mp="7ezavu2i6dqd">
                                  Zone {deliveryZone}
                                </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-507" data-mp="3tlnut5okde3m">
                                  Outside standard zones (Custom Quote)
                                </p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-508" data-mp="3of8zl1o5s4de">
                            <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-509" data-mp="3tfn9kp9bloe2" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-510" data-mp="29c50j53q5hz">
                              {phone}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-511" data-mp="35psvk4d9lts8">
                            <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-512" data-mp="35nznqlqx13in" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-513" data-mp="3ancbd8pm2ema">
                              {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        }) : 'No date selected'}
                              {deliveryTime && <>
                                  <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-514" data-mp="2a0twzdy2bgf8">
                                    •
                                  </span>
                                  <span className="capitalize" data-id="element-515" data-mp="2jpv9gu5effog">
                                    {deliveryTime}
                                  </span>
                                </>}
                            </p>
                          </div>
                          {instructions && <div className="flex items-start gap-3 pt-3 border-t border-stone-200 dark:border-white/5" data-id="element-516" data-mp="1r30rq4zyuueo">
                              <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-517" data-mp="25w43fvqvmdxh" />
                              <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-518" data-mp="211qadzzosivz">
                                "{instructions}"
                              </p>
                            </div>}
                        </div>
                      </div>

                      {/* Payment Method + Place Order */}
                      <div data-id="element-519" data-mp="3oy67vot82rja">
                        <div className="flex items-baseline justify-between mb-3" data-id="element-520" data-mp="3oyg3brx1dqwd">
                          <h3 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white tracking-tight" data-id="element-521" data-mp="3ayy7islbpddi">
                            Pay on Delivery
                          </h3>
                          <span className="text-xs text-stone-500 dark:text-stone-400 font-medium" data-id="element-522" data-mp="6jozuekxgtyf">
                            Online payment coming soon
                          </span>
                        </div>
                        <p className="text-sm text-brand-muted dark:text-stone-400 mb-3" data-id="element-523" data-mp="3tvzh6qpto1ns">
                          Choose how you'll pay your driver, then place your
                          order
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-524" data-mp="u8q0vgrfl2to">
                          {([{
                      id: 'cash',
                      label: 'Cash',
                      icon: Banknote
                    }, {
                      id: 'card',
                      label: 'Card',
                      icon: CreditCard
                    }] as const).map(opt => {
                      const isActive = payOnDeliverySubMethod === opt.id;
                      const Icon = opt.icon;
                      return <button key={opt.id} type="button" onClick={() => {
                        setPaymentMethod('delivery');
                        setPayOnDeliverySubMethod(opt.id);
                      }} className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${isActive ? 'border-[#059F40] bg-[#059F40]/10 text-[#059F40] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : 'border-stone-200 dark:border-white/5 bg-white/50 dark:bg-[#1a1a1a] text-brand-charcoal dark:text-stone-200 hover:border-stone-300 dark:hover:border-white/20 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'}`} aria-pressed={isActive} data-id="element-525" data-mp="ohhj0rjqriwk">
                                <Icon className="w-4 h-4 shrink-0" data-id="element-526" data-mp="1eqoyivchj0mm" />
                                <span data-id="element-527" data-mp="1eovt1jdlymg7">{opt.label}</span>
                              </button>;
                    })}
                        </div>
                        {(() => {
                    const placeOrderDisabled = !isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim() || isProcessingPayment;
                    const placeOrderReady = !placeOrderDisabled;
                    return <motion.button type="submit" disabled={placeOrderDisabled} animate={placeOrderReady ? {
                      boxShadow: ['inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 0 rgba(5,159,64,0.55), 0 4px 14px rgba(5,159,64,0.4)', 'inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 10px rgba(5,159,64,0), 0 4px 14px rgba(5,159,64,0.4)', 'inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 0 rgba(5,159,64,0), 0 4px 14px rgba(5,159,64,0.4)']
                    } : {
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 1px 2px rgba(5,159,64,0.15)'
                    }} transition={placeOrderReady ? {
                      duration: 1.8,
                      repeat: Infinity,
                      ease: 'easeOut'
                    } : {
                      duration: 0.2
                    }} whileTap={placeOrderReady ? {
                      scale: 0.98
                    } : undefined} className="relative w-full flex items-center justify-center gap-2 px-4 py-7 rounded-lg text-base font-bold transition-colors bg-[#059F40] hover:bg-[#17A347] text-white disabled:opacity-50 disabled:cursor-not-allowed mb-5" data-id="element-528" data-mp="22gj8n55f6b36">
                              {isProcessingPayment ? <>
                                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-529" data-mp="2s67abe7yyl78" />
                                  <span data-id="element-530" data-mp="2s8a4mag5n0hr">Processing</span>
                                </> : <>
                                  <Check className="w-5 h-5" strokeWidth={3} data-id="element-531" data-mp="2x2nxo67cgvj9" />
                                  <span data-id="element-532" data-mp="1f5kk7bj00jv9">Place Order</span>
                                </>}
                            </motion.button>;
                  })()}
                        {/* Submission error — surfaces a real, actionable
                         message when an order fails to reach dispatch
                         (network, EmailJS rate-limit, etc.) instead of
                         silently failing. Includes a tap-to-call so the
                         customer can still book over the phone. */}
                        {orderError && !isProcessingPayment && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="mt-3 rounded-xl border border-red-400/60 bg-red-50 dark:bg-red-950/30 p-4" role="alert" aria-live="polite" data-id="element-533" data-mp="159hg404l6b5o">
                            <div className="flex items-start gap-3" data-id="element-534" data-mp="8r32abgi65xf">
                              <Info className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-535" data-mp="27v1tc8hfi1m3" />
                              <div className="flex-1 min-w-0" data-id="element-536" data-mp="2cu4p12biqvhe">
                                <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1" data-id="element-537" data-mp="2hm68b873tmgx">
                                  Couldn't place your order
                                </p>
                                <p className="text-sm text-red-700/90 dark:text-red-200/90 leading-relaxed mb-3" data-id="element-538" data-mp="1oxh2ivv3ssyu">
                                  {orderError}
                                </p>
                                <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors" data-id="element-539" data-mp="1yvethr4cl3v5">
                                  <Phone className="w-3.5 h-3.5" data-id="element-540" data-mp="3aatxheuz4vzr" />
                                  Call (206) 619-3742
                                </a>
                              </div>
                            </div>
                          </motion.div>}
                      </div>

                      {/* SMS Opt-in */}
                      <div className="p-6 border-b border-stone-100 dark:border-stone-800" data-id="element-541" data-mp="yo9luoqmj577">
                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-542" data-mp="tvq8nsvumc2x">
                          <div className="relative flex items-center justify-center mt-0.5" data-id="element-543" data-mp="oxx5l8pudy6v">
                            <input type="checkbox" checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} className="peer sr-only" data-id="element-544" data-mp="jytyliqaxhw9" />
                            <div className="w-5 h-5 border-2 border-stone-300 dark:border-stone-600 rounded transition-colors peer-checked:bg-brand-orange peer-checked:border-brand-orange group-hover:border-brand-orange" data-id="element-545" data-mp="fv5rt29huqc2"></div>
                            <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-546" data-mp="60vz1jgn3q0t" />
                          </div>
                          <div data-id="element-547" data-mp="20ahg6ffh8v5x">
                            <span className="text-sm font-medium text-stone-900 dark:text-white block mb-0.5" data-id="element-548" data-mp="xzfjjztq4p3w">
                              Send me SMS updates about this delivery
                            </span>
                            <span className="text-xs text-stone-500 dark:text-stone-400" data-id="element-549" data-mp="o1rnn6kdtbk5">
                              Get notified when your driver is assigned, en
                              route, and delivered.
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Total reminder */}
                      <div className="px-6 pb-6" data-id="element-550" data-mp="5b0i6g3im5b1">
                        <div className="flex items-center justify-between text-sm" data-id="element-551" data-mp="5cblket1tb1m">
                          <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-552" data-mp="aad0qoncsq7t">
                            Order total
                          </span>
                          <span className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-553" data-mp="bxa0ms8niyu">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW — fallback (no summary data) =====
               Safety net: handleSubmitOrder only flips view→'success'
               AFTER setOrderSummaryData runs AND the email was accepted.
               But if it somehow happens (state leak, future refactor),
               show a real message + a way to reach dispatch instead of
               a blank screen behind the "Order Confirmed" header. */}
              {view === 'success' && !orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-554" data-mp="4ews7cctuppv">
                  <div className="flex flex-col items-center text-center space-y-6 max-w-md mx-auto py-8" data-id="element-555" data-mp="4hj0xfbgfb9o">
                    <div className="w-20 h-20 bg-amber-100 dark:bg-[#1a1a1a] rounded-full flex items-center justify-center" data-id="element-556" data-mp="edcmtpb2ch44">
                      <Info className="w-10 h-10 text-amber-600 dark:text-amber-400" data-id="element-557" data-mp="lhc1um7gw6gw" />
                    </div>
                    <div data-id="element-558" data-mp="qgoytzs1ytx7">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-559" data-mp="qfx4esa6z28i">
                        We may have lost your order details
                      </h3>
                      <p className="text-base text-brand-muted dark:text-stone-300" data-id="element-560" data-mp="106rrtzz6c7vj">
                        Your confirmation didn't load on this device. If you
                        placed an order, please call us to verify it went
                        through — we'll find it on our end.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 w-full" data-id="element-561" data-mp="1j4ujynn0qkxk">
                      <a href="tel:206-619-3742" className="w-full py-3.5 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-562" data-mp="1j1yod5yn88ca">
                        <Phone className="w-5 h-5" data-id="element-563" data-mp="3dc6hy33bombn" />
                        Call (206) 619-3742
                      </a>
                      <button type="button" onClick={() => {
                  setView('cart');
                  setOrderError('');
                }} className="w-full py-3 bg-stone-200 dark:bg-[#1a1a1a] hover:bg-stone-300 dark:hover:bg-white/10 text-brand-charcoal dark:text-white font-bold rounded-xl transition-colors" data-id="element-564" data-mp="38kwtv6xf9r1v">
                        Back to cart
                      </button>
                    </div>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-565" data-mp="3mb0drr8tx8o5">
                  <div className="flex flex-col items-center text-center mb-8" data-id="element-566" data-mp="3hcqf36nwbpxs">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20
              }} className="w-24 h-24 bg-green-100 dark:bg-[#1a1a1a] rounded-full flex items-center justify-center mb-6 relative" data-id="element-567" data-mp="2ay7msinv4o95">
                      <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-568" data-mp="30jh6pyhmls1n" />
                      <motion.div initial={{
                  scale: 1,
                  opacity: 0.6
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} className="absolute inset-0 rounded-full border-2 border-green-500" data-id="element-569" data-mp="2mof2dfsfgaqy" />
                    </motion.div>
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-570" data-mp="39lgnqqsn4w4l">
                      Order Received!
                    </h3>
                    <p className="text-lg text-brand-muted dark:text-stone-300 mb-4 max-w-md" data-id="element-571" data-mp="5xon07uortib">
                      Thank you, {orderSummaryData.name}. We've received your
                      order request for{' '}
                      {orderSummaryData.materialYards ?? orderSummaryData.totalYards}{' '}
                      yards of material
                      {orderSummaryData.yardsBilledExtra > 0 && <span className="text-amber-600 dark:text-amber-400" data-id="element-572" data-mp="1veah6ei7q03t">
                          {' '}
                          (billed as {orderSummaryData.totalYards} yd — 3 yd
                          order min)
                        </span>}
                      .
                    </p>
                    <div className="inline-flex items-center gap-2 bg-brand-orange/10 border border-brand-orange/20 px-4 py-2 rounded-full" data-id="element-573" data-mp="1hnfg3odygnas">
                      <span className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider" data-id="element-574" data-mp="1cmrgiebnuy4y">
                        Confirmation #
                      </span>
                      <span className="font-mono font-extrabold text-brand-orange" data-id="element-575" data-mp="2o2pt2s61s2ax">
                        {orderSummaryData.orderNumber}
                      </span>
                    </div>
                  </div>

                  {/* Optional drop-site photo — moved here from checkout so
                   the form stays fast. Customer can take their time and
                   we send it to dispatch as a follow-up tied to the
                   order number. The Property & Vehicle Safety reminder now
                   sits alongside it (moved here from the checkout form) so
                   the constraint lands with the drop-site photo prompt — and
                   it's also repeated in the confirmation email. */}
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-start" data-id="element-576" data-mp="3trnvv7ua95l">
                    <PostOrderDropSitePhoto orderNumber={orderSummaryData.orderNumber} customerName={orderSummaryData.name} customerEmail={orderSummaryData.email} address={orderSummaryData.addressInput} data-id="element-577" data-mp="3w4dmuju9nt7" />
                    <div className="flex items-start gap-2.5 p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-left h-full" data-id="element-578" data-mp="1npysw46f2v92">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" data-id="element-579" data-mp="1rpfzj0xkxyft" />
                      <div data-id="element-580" data-mp="1dtvt34z1f8pa">
                        <h5 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1" data-id="element-581" data-mp="1iqm6fkjgjq70">
                          Property & Vehicle Safety
                        </h5>
                        <p className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed" data-id="element-582" data-mp="2bf9utozi6vos">
                          We cannot drive across irrigated, wet, frozen, or
                          landscaped areas to reach the dump spot. Please make
                          sure your instructions keep our truck on gravel or
                          paved surfaces.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* What happens next — stepped timeline */}
                  <div className="bg-[#e6f5ea] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-2xl p-6 mb-6 text-left" data-id="element-583" data-mp="341hrxpoxx1t8">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-5 flex items-center gap-2" data-id="element-584" data-mp="2u3k2wzz9b0zk">
                      <MessageSquare className="w-5 h-5 text-brand-orange" data-id="element-585" data-mp="2q2rug8y7l04c" />
                      What happens next?
                    </h4>
                    <div className="space-y-4" data-id="element-586" data-mp="2l4qe3vs63ewk">
                      {[{
                  icon: Send,
                  title: 'SMS confirmation',
                  desc: <>
                              Text confirmation arriving at{' '}
                              <strong className="text-brand-charcoal dark:text-white" data-id="element-587" data-mp="26ergbu4iy5hm">
                                {formatPhone(orderSummaryData.phone)}
                              </strong>{' '}
                              within a few minutes.
                            </>,
                  active: true
                }, {
                  icon: ClipboardCheck,
                  title: 'Dispatch review',
                  desc: 'Our team verifies your address, materials, and delivery window — usually within 1 business hour.'
                }, {
                  icon: Truck,
                  title: 'Driver dispatched',
                  desc: 'On delivery day, we text you a live tracking link the moment your driver is en route.'
                }].map((step, i) => {
                  const Icon = step.icon;
                  return <div key={i} className="flex gap-4" data-id="element-588" data-mp="3uctlv5d7l0xt" data-mpi={i}>
                            <div className="flex flex-col items-center" data-id="element-589" data-mp="35z764pkzxy9">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${step.active ? 'bg-[#059F40] text-white shadow-[0_0_12px_rgba(5,159,64,0.4)]' : 'bg-stone-200 dark:bg-[#1a1a1a] text-stone-500 dark:text-stone-400'}`} data-id="element-590" data-mp="33w7esymmqgc">
                                <Icon className="w-4 h-4" data-id="element-591" data-mp="3bk7rkphy9ifc" />
                              </div>
                              {i < 2 && <div className="w-px flex-1 bg-stone-200 dark:bg-white/5 my-1 min-h-[18px]" data-id="element-592" data-mp="1i3bjmk0e3h4f" />}
                            </div>
                            <div className="pb-1 flex-1" data-id="element-593" data-mp="1qv3rqo1xye5u">
                              <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-594" data-mp="1qwojk5wvfj00">
                                {step.title}
                              </p>
                              <p className="text-sm text-brand-muted dark:text-stone-400 mt-0.5 leading-relaxed" data-id="element-595" data-mp="20s8gmvn68nqc">
                                {step.desc}
                              </p>
                            </div>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Order details card */}
                  <div className="bg-[#e6f5ea] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-2xl p-6 mb-6 text-left" data-id="element-596" data-mp="ffoudj5tlka7">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-597" data-mp="bfxwkvhvm3w6">
                      Order Details
                    </h4>
                    <div className="space-y-3 mb-4" data-id="element-598" data-mp="5brz6e9dl5o4">
                      {orderSummaryData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm font-medium" data-id="element-599" data-mp="3nq9ca2wj1tfu" data-mpi={i}>
                          <span className="text-brand-charcoal dark:text-stone-200" data-id="element-600" data-mp="3soua5vob4114">
                            {item.quantity} yds — {item.product}
                          </span>
                        </div>)}
                    </div>
                    <div className="space-y-2 text-sm font-medium border-t border-stone-200 dark:border-white/5 pt-4 mb-4" data-id="element-601" data-mp="3irpm1qawhvho">
                      <div className="flex justify-between" data-id="element-602" data-mp="34szo68g9brs9">
                        <span className="text-brand-muted dark:text-white" data-id="element-603" data-mp="39pgftcmo0oa3">
                          Materials Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-604" data-mp="12xpey3wdewwd">
                          ${orderSummaryData.totalPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-605" data-mp="15hvgk4xiib9r">
                        <span className="text-brand-muted dark:text-white" data-id="element-606" data-mp="11htkcbajqa1v">
                          Delivery Fee
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-607" data-mp="rkf8yq3hcbgf">
                          {orderSummaryData.deliveryFeeLabel}
                        </span>
                      </div>
                      {orderSummaryData.saturdaySurcharge > 0 && <div className="flex justify-between" data-id="element-608" data-mp="1ka5ql94blvha">
                          <span className="text-brand-muted dark:text-white" data-id="element-609" data-mp="1fi4f3pez88sb">
                            Saturday Delivery
                          </span>
                          <span className="text-brand-charcoal dark:text-white" data-id="element-610" data-mp="1aj18i0jclui8">
                            ${orderSummaryData.saturdaySurcharge.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between" data-id="element-611" data-mp="2365i6itdyx0c">
                        <span className="text-brand-muted dark:text-white" data-id="element-612" data-mp="2akeqw93vte29">
                          Sales Tax
                          <span className="text-xs text-brand-muted dark:text-white/70 ml-1" data-id="element-613" data-mp="2fipz6fej3gid">
                            (est. {(TAX_RATE * 100).toFixed(1)}%)
                          </span>
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-614" data-mp="2pe16wdrirngm">
                          ${(orderSummaryData.estimatedTax ?? 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200 dark:border-white/5" data-id="element-615" data-mp="337sf9itil1ar">
                        <span className="text-brand-charcoal dark:text-white" data-id="element-616" data-mp="385tz45yrl4na">
                          Estimated Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-617" data-mp="3d45awm8mdp85">
                          ${orderSummaryData.finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm font-medium border-t border-stone-200 dark:border-white/5 pt-4" data-id="element-618" data-mp="2c293oz6c16d">
                      <div className="flex items-start gap-3" data-id="element-619" data-mp="ga8yegq24suf">
                        <MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-620" data-mp="bcgu0n887e9c" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-621" data-mp="bae380z91h3k">
                          {orderSummaryData.addressInput}
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-622" data-mp="3er34b7g2bylu">
                        <CalendarDays className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-623" data-mp="3tjepw2w5aclr" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-624" data-mp="3oqbv5rfmsx3z">
                          {new Date(orderSummaryData.deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                          •{' '}
                          <span className="capitalize" data-id="element-625" data-mp="1gbnizpxz5tu6">
                            {orderSummaryData.timeLabel}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-626" data-mp="16ksvyjcwmq7o">
                        <Phone className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-627" data-mp="16iy8axa90jzw" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-628" data-mp="1uboltaxsvbca">
                          {formatPhone(orderSummaryData.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Prep tip link */}
                  <button type="button" onClick={() => {
              handleCloseSuccess();
              navigate('/delivery-prep');
            }} className="w-full flex items-center justify-between bg-[#f0faf3] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-xl p-4 mb-3 hover:bg-[#e6f5ea] dark:hover:bg-white/10 transition-colors text-left" data-id="element-629" data-mp="1q7grmy8kp36o">
                    <div className="flex items-center gap-3" data-id="element-630" data-mp="3e0at7ic7judz">
                      <Info className="w-5 h-5 text-brand-orange shrink-0" data-id="element-631" data-mp="3j0gkuyih9gw2" />
                      <div data-id="element-632" data-mp="3iyc7lpyov5j4">
                        <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-633" data-mp="3n1s3ul6uj3va">
                          How to prep for delivery
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400" data-id="element-634" data-mp="2q9yardjc6f95">
                          Driveway clearance, dump zone, what to expect
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-muted" data-id="element-635" data-mp="343piqhhf5r2r" />
                  </button>

                  {/* Action button */}
                  <button onClick={handleCloseSuccess} className="w-full bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white py-4 rounded-xl font-extrabold text-lg transition-colors border border-transparent dark:border-white/5 backdrop-blur-sm mt-4" data-id="element-636" data-mp="27kryo9d1slwb">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}